
import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { PurchaseOrderModel, IPurchaseOrder, IPurchaseOrderItem, IPurchaseOrderCostData, IPurchaseOrderHistoryItem, IPurchaseOrderFinancial } from '@/models/purchase_order';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationModel } from '@/models/store_location';
import { StoreLocationList } from '@/collections/store_locations';
import dateFormat from 'dateformat';
import { jsPDF } from 'jspdf';
import { PurchaseReceived, IPurchaseReceived, IPurchaseReceivedItem } from '@/models/purchase_received';
import { EventBus } from '@/main';
import { ax, getIdentityToken, get, post, put, del } from '@/services/http';
import ImportItemsDialog from '@/components/purchase-order/ImportItemsDialog.vue';
import CostSummaryDialog from '@/components/purchase-order/CostSummaryDialog.vue';
import PurchaseOrderAttachments from '@/components/purchase-order/PurchaseOrderAttachments.vue';
import POStatusTimeline from '@/components/purchase-order/POStatusTimeline.vue';
import POLifecycleGuide from '@/components/purchase-order/POLifecycleGuide.vue';
import POFinancialEditDialog from '@/components/purchase-order/POFinancialEditDialog.vue';
import LandedCostBreakdown from '@/components/purchase-order/LandedCostBreakdown.vue';
import AddProductsDialog from '@/components/purchase-order/AddProductsDialog.vue';
import { SupplierList } from '@/collections/suppliers';
import { FilterColumnModel } from '@/models/filter_column';
import { FilterColumnList } from '@/collections/filter_columns';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
// Constants for Shopify sync retry logic
const RETRYABLE_SYNC_STATUSES = ['failed', 'pending', 'partial'] as const;

/**
 * Helper function to determine if a sync status is retryable
 * @param status - The sync status to check (can be undefined or null)
 * @returns true if the status is retryable, false otherwise
 */
function isRetryableStatus(status?: string | null): boolean {
  if (!status) return false;
  return RETRYABLE_SYNC_STATUSES.includes(status as any);
}

// Phase 1: Shared column codes - maps internal column IDs to API field names
// These codes MUST match what the MasterData API returns in column.code
const COLUMN_API_CODES: { [key: string]: string } = {
  // PO Status (from PO line items, not MasterData)
  received_quantity: 'receivedQuantity',
  returned_quantity: 'returnedQuantity',
  units_left: 'unitsLeft', // Calculated: orderedQuantity - receivedQuantity

  // Basic Information
  variant_id: 'variant_id',
  product_id: 'product_id',
  item_description: 'Item[Item Description]',
  product_barcode: 'product_barcode',
  vendor_name: 'Vendor Name',
  product_type: 'product_type',
  product_status: 'product_status',

  // Pricing
  unit_cost: 'unit_cost',
  selling_price: 'unit_price',
  retail_value: '[retail_value]',
  actual_inventory_cost: '[Actual Inventory Cost]',

  // Quantities
  actual_inventory_quantity: '[Actual Inventory Quantity]',
  committed_quantity: '[committed_quantity]',
  incoming_quantity: '[incoming_quantity]',
  available_quantity: '[available_quantity]',

  // Product Attributes
  total_weight: 'total_weight',
  weight_unit: 'weight_unit',
  hs_code: 'hs_code',
  country_of_origin: 'country_of_origin',
  inventory_tracking: 'inventory_tracking',
  continue_selling_out_stock: 'continue_selling_out_stock',

  // Metadata
  product_tags: 'product_tags',
  product_collections: 'product_collections',
  product_published_date: 'product_published_date'
};

// Columns that come from PO line items instead of MasterData
const PO_ITEM_COLUMNS = ['note', 'received_quantity', 'returned_quantity', 'units_left', 'tax_type', 'tax_rate', 'tax_amount', 'landed_cost', 'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit', 'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'];

// Numeric columns that should be right-aligned
const NUMERIC_COLUMNS = [
  'received_quantity', 'returned_quantity', 'units_left',
  'actual_inventory_quantity', 'committed_quantity', 'incoming_quantity', 'available_quantity',
  'unit_cost', 'selling_price', 'retail_value', 'actual_inventory_cost',
  'tax_rate', 'tax_amount', 'landed_cost',
  'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit',
  'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'
];

// Reverse mapping: API code -> column ID (for parsing API responses)
const COLUMN_ID_BY_API_CODE: { [key: string]: string } = Object.fromEntries(
  Object.entries(COLUMN_API_CODES).map(([id, code]) => [code, id])
);

// Phase 1: Column metadata for optional MasterData columns
const COLUMN_METADATA: { [key: string]: { label: string; category: string } } = {
  // PO Line Item Fields (from PO line items)
  note: { label: 'Note', category: 'po_line_item' },
  received_quantity: { label: 'Received Quantity', category: 'po_line_item' },
  returned_quantity: { label: 'Returned Quantity', category: 'po_line_item' },
  units_left: { label: 'Units Left', category: 'po_line_item' },
  tax_type: { label: 'Tax Type', category: 'po_line_item' },
  tax_rate: { label: 'Tax %', category: 'po_line_item' },
  tax_amount: { label: 'Tax Amount', category: 'po_line_item' },
  landed_cost: { label: 'Landed Cost', category: 'po_line_item' },
  landed_cost_shipping_per_unit: { label: 'Shipping / Unit', category: 'po_line_item' },
  landed_cost_adjustment_per_unit: { label: 'Adjustment / Unit', category: 'po_line_item' },
  margin_amount: { label: 'Margin', category: 'po_line_item' },
  margin_percent: { label: 'Margin %', category: 'po_line_item' },
  landed_margin_amount: { label: 'Landed Margin', category: 'po_line_item' },
  landed_margin_percent: { label: 'Landed Margin %', category: 'po_line_item' },

  // Basic Information
  variant_id: { label: 'Variant ID', category: 'basic' },
  product_id: { label: 'Product ID', category: 'basic' },
  item_description: { label: 'Item Description', category: 'basic' },
  product_barcode: { label: 'Product Barcode', category: 'basic' },
  vendor_name: { label: 'Vendor Name', category: 'basic' },
  product_type: { label: 'Product Type', category: 'basic' },
  product_status: { label: 'Product Status', category: 'basic' },

  // Pricing
  unit_cost: { label: 'Unit Cost', category: 'pricing' },
  selling_price: { label: 'Selling Price', category: 'pricing' },
  retail_value: { label: 'Retail Value', category: 'pricing' },
  actual_inventory_cost: { label: 'Actual Inventory Cost', category: 'pricing' },

  // Quantities
  actual_inventory_quantity: { label: 'Actual Inventory Quantity', category: 'quantities' },
  committed_quantity: { label: 'Committed Quantity', category: 'quantities' },
  incoming_quantity: { label: 'Incoming Quantity', category: 'quantities' },
  available_quantity: { label: 'Available Quantity', category: 'quantities' },

  // Product Attributes
  total_weight: { label: 'Total Weight', category: 'attributes' },
  weight_unit: { label: 'Weight Unit', category: 'attributes' },
  hs_code: { label: 'HS Code', category: 'attributes' },
  country_of_origin: { label: 'Country of Origin', category: 'attributes' },
  inventory_tracking: { label: 'Inventory Tracking', category: 'attributes' },
  continue_selling_out_stock: { label: 'Continue Selling Out of Stock', category: 'attributes' },

  // Metadata
  product_tags: { label: 'Product Tags', category: 'metadata' },
  product_collections: { label: 'Product Collections', category: 'metadata' },
  product_published_date: { label: 'Product Published Date', category: 'metadata' },
};

@Component({
  components: {
    ImportItemsDialog,
    CostSummaryDialog,
    PurchaseOrderAttachments,
    POStatusTimeline,
    POLifecycleGuide,
    POFinancialEditDialog,
    LandedCostBreakdown,
    AddProductsDialog
  }
})
export default class PurchaseOrderDetail extends Vue {
  @Prop({ type: [String, Number], required: true }) id!: string | number;

  @Prop({ type: Object, required: true }) store!: StoreModel;
  private purchaseOrder: PurchaseOrderModel | null = null;
  private supplier: SupplierModel | null = null;
  private location: StoreLocationModel | null = null;
  private locations: StoreLocationModel[] = [];
  private items: IPurchaseOrderItem[] = [];
  private editedPurchaseOrder: Partial<IPurchaseOrder> = {};
  private isEditing = false;
  private loading = false;
  private purchaseOrderNotFound = false;
  private errorMessage = '';
  private hasError = false;
  private saving = false;
  private duplicating = false;
  private showDuplicateDialog = false;
  private deletingItemId: number | null = null;
  private dateMenu = false;
  private departureDateMenu = false;
  private editedDepartureDate = '';
  private internalNotesEditValue = '';
  private showFinancialEditDialog = false;
  private suppliers: SupplierModel[] = [];
  private loadingSuppliers = false;

  // Bill To / Ship To editing
  private showAddressDialog = false;
  private editingAddressType: 'bill_to' | 'ship_to' = 'bill_to';
  private editedAddress: Record<string, string> = {};
  private savingAddress = false;
  private resolvedBillTo: Record<string, string> | null = null;
  private resolvedShipTo: Record<string, string> | null = null;
  private billToSource: 'database' | 'default' = 'default';
  private shipToSource: 'database' | 'default' = 'default';
  private showTransactionHistory = false;
  private showActivityHistory = false;
  private addressSuggestions: Record<string, string>[] = [];
  private loadingAddressSuggestions = false;
  private showResetAddressDialog = false;
  private resetAddressType: 'bill_to' | 'ship_to' = 'bill_to';
  private resettingAddress = false;
  private taxTypeList: TaxTypeList = new TaxTypeList();
  private bulkTaxTypeId: number | null = null;
  private showBulkTaxMenu = false;

  // Phase 1: PO Line Items Column Customization
  private columnConfig: { columns: {[columnId: string]: {enabled: boolean, order: number}} } | null = null;
  private masterData: {[variantId: string]: any} = {};
  private loadingConfig = false;
  private loadingMasterData = false;

  // Phase 2: Custom Columns
  private customColumnDefinitions: {[columnId: string]: {label: string, enabled: boolean, order: number}} = {};
  private customColumnValues: {[variantId: string]: {[columnId: string]: string}} = {};
  private loadingCustomColumns = false;

  // Phase 3: Metafield Columns
  private metafieldColumns: {[key: string]: {enabled: boolean, order: number, label: string}} = {};
  private metafieldValues: {[productId: string]: {[key: string]: string}} = {};
  private loadingMetafieldValues = false;

  // Email dialog data
  private showEmailDialog = false;
  private emailSubject = '';
  private emailMessage = '';
  private emailRecipients: string[] = [];
  private attachmentFormat: 'pdf' | 'csv' = 'pdf';
  private includePricing = true;
  private replyToEmails: string[] = [];
  private companyName = '';
  private savedEmails: string[] = [];
  private savedCompanyNames: string[] = [];
  private sending = false;
  private isEmailFormValid = false;

  // Cancel PO dialog
  private showCancelDialog = false;
  private canceling = false;

  // Revert to Draft dialog
  private showRevertToDraftDialog = false;
  private revertingToDraft = false;

  // PO Lifecycle Guide dialog
  private showLifecycleGuide = false;

  // Edit mode dialogs
  private showSaveChangesDialog = false;
  private showDiscardChangesDialog = false;

  // Export dialog data
  private showExportDialog = false;
  private exportFormat: 'csv' | 'xlsx' | 'pdf' = 'csv';
  private exporting = false;
  private exportIncludePricing = true;

  // Import items dialog
  private showImportItemsDialog = false;

  // Add products dialog
  private showAddProductsDialog = false;

  // Mark as Completed dialog
  private showMarkAsCompletedDialog = false;
  private markingAsCompleted = false;

  // Audit History (status changes, email events)
  private auditHistory: IPurchaseOrderHistoryItem[] = [];
  private loadingAuditHistory = false;

  // Receive dialog data
  private showReceiveDialog = false;
  private markAsCompleteAfterReceive = false;
  private receiving = false;
  private receiveQuantities: { [key: number]: number | string } = {};
  private receiveNotes = '';
  private receiveds: PurchaseReceived[] = [];
  private loadingHistory = false;
  private deliveryNoteNumber = '';
  private showReceiveDetailDialog = false;
  private selectedReceive: IPurchaseReceived | null = null;
  private showReceiveReviewDialog = false;
  private updateShopifyInventory = true;
  private shopifyCostUpdateMethod = 'none';
  private receiveCosts: { [key: number]: number } = {};
  private receiveSearchQuery = '';
  
  // Add mode switcher state
  private receiveMode: 'manual' | 'import' = 'manual';
  
  // Scroll state for settings indicator
  private showSettingsIndicator = false;

  // Import-related data properties
  private importFile: File | null = null;
  private importingReceive = false;
  private downloadingTemplate = false;

  // Add these properties
  private confirming = false;

  // Import summary data
  private importSummaryData: {
    totalRowsInCsv: number;
    successfullyProcessedRows: number;
    failedRows: number;
    errorDetails: any[];
    receiveRecordId: number | null;
    inventoryUpdateStatus: string;
    costUpdateStatus: string;
    costCalculationMethod: string | null;
    itemsWithCosts: number;
  } = {
    totalRowsInCsv: 0,
    successfullyProcessedRows: 0,
    failedRows: 0,
    errorDetails: [],
    receiveRecordId: null,
    inventoryUpdateStatus: 'skipped',
    costUpdateStatus: 'skipped',
    costCalculationMethod: null,
    itemsWithCosts: 0
  };
  private showImportSummaryDialog = false;
  private retryingSyncStatus = false;
  private retryingCostSyncStatus = false;
  private retryingInventorySync = false;
  private retryingCostSync = false;
  private retryingHistoryInventory: number | null = null;
  private retryingHistoryCost: number | null = null;

  // Return dialog data
  private showReturnDialog = false;
  private showReturnReviewDialog = false;
  private returnQuantities: { [key: number]: number } = {};
  private returnQuantityErrors: { [key: number]: string } = {};
  private returnReason = '';
  private returnNotes = '';
  private submittingReturn = false;
  private returnSearchQuery = '';

  // Return history data
  private returnHistory: any[] = [];
  private loadingReturnHistory = false;
  private selectedReturn: any = null;
  private showReturnDetailDialog = false;
  private showDeleteReturnDialog = false;
  private returnToDelete: any = null;
  private retryingReturnSync: number | null = null;
  private deletingReturn = false;

  // File error tracking
  private fileError: string = '';

  // Cost tracking data
  private showCostSummaryDialog = false;
  private costSummary: any = {};
  private costDetails: any[] = [];
  private shopifyUpdateResult: any = {};

  private shopifyCostUpdateOptions = [
    { text: 'Do not update', value: 'none' },
    { text: 'Latest cost', value: 'latest' },
    { text: 'Weighted average', value: 'average' }
  ];

  // Variant order quantity constraints
  private variantOrderConstraints: { [variantId: number]: { min: number; max: number } } = {};
  private loadingConstraints = false;
  private quantityValidationErrors: { [itemId: number]: string } = {};

  // Validation Rules
  private emailSubjectRules = [(v: string) => !!v || 'Subject is required'];

  private replyToRules = [
    (v: string[]) => !v || v.length === 0 || v.every((email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) || 'Invalid email format'
  ];

  private emailRecipientsRules = [
    (v: string[]) => v.length > 0 || 'At least one recipient is required',
    (v: string[]) => v.every((email: string) => this.validateEmail(email)) || 'Invalid email format',
  ];

  private get emailMessageRules() {
    return [(v: string) => !!v || 'Message is required'];
  }

  private get attachmentFormatRules() {
    return [(v: string) => !!v || 'A format must be selected'];
  }

  // Computed property for localStorage key (store-specific)
  private get localStorageKey(): string {
    return `po_email_preferences_${this.store.id}`;
  }

  private get savedEmailsKey(): string {
    return `po_saved_emails_${this.store.id}`;
  }

  private get savedCompanyNamesKey(): string {
    return `po_saved_company_names_${this.store.id}`;
  }

  // Computed property for formatted date in edit mode
  get formattedExpectedDeliveryDateForEdit(): string {
    return this.formatDate(this.editedPurchaseOrder.expectedDeliveryDate);
  }

  // Computed property for formatted departure date in edit mode
  get formattedDepartureDateForEdit(): string {
    return this.editedDepartureDate ? this.formatDate(this.editedDepartureDate) : '';
  }

  // Computed property for loading column data (MasterData, Custom, Metafield)
  get loadingColumnData(): boolean {
    return this.loadingMasterData || this.loadingCustomColumns || this.loadingMetafieldValues;
  }

  // Helper to format note value - returns dash for empty/invalid values
  formatNote(note: any): string {
    if (note === null || note === undefined || note === '' || note === 0 || note === '0') {
      return '—';
    }
    const noteStr = String(note).trim();
    return noteStr === '' || noteStr === '0' ? '—' : noteStr;
  }

  // Helper to check if note has a valid value
  hasValidNote(note: any): boolean {
    if (note === null || note === undefined || note === '' || note === 0 || note === '0') {
      return false;
    }
    const noteStr = String(note).trim();
    return noteStr !== '' && noteStr !== '0';
  }

  // Helper to check if a column is enabled in config
  // For PO status columns (received_quantity, returned_quantity, units_left), default to true for backward compatibility
  // For 'note' column, default to false (disabled by default)
  isColumnEnabled(columnId: string): boolean {
    const config = this.columnConfig?.columns?.[columnId];
    if (config === undefined) {
      // These columns should be disabled by default when not in saved config
      if (columnId === 'note') return false;
      if (columnId === 'landed_cost_shipping_per_unit') return false;
      if (columnId === 'landed_cost_adjustment_per_unit') return false;
      if (columnId === 'margin_amount') return false;
      if (columnId === 'margin_percent') return false;
      if (columnId === 'landed_margin_amount') return false;
      if (columnId === 'landed_margin_percent') return false;
      // Other columns default to true for backward compatibility
      return true;
    }
    return config.enabled;
  }

  private get headers() {
    // Helper to get configured order for a column, with fallback
    const getOrder = (columnId: string, defaultOrder: number): number => {
      return this.columnConfig?.columns?.[columnId]?.order ?? defaultOrder;
    };

    const quantityHeader = this.purchaseOrder?.status === 'draft'
      ? { text: 'Quantity', value: 'quantityStatus', sortable: false, align: 'center', width: '120px', order: getOrder('req_quantity', 4) }
      : { text: 'Quantity Status', value: 'quantityStatus', sortable: false, align: 'center', width: '180px', order: getOrder('req_quantity', 4) };

    // Dynamic currency suffix for monetary columns
    const currSuffix = this.isForeignCurrencyPO ? ` (${this.purchaseOrder!.currencyCode})` : '';

    const baseHeaders: any[] = [
      { text: '', value: 'variant_image', sortable: false, align: 'center', width: '70px', order: getOrder('req_image', 1) },
      { text: 'Product Details', value: 'productDetails', sortable: false, align: 'start', width: '35%', order: getOrder('req_product_details', 2) },
      quantityHeader,
      { text: `Unit Price${currSuffix}`, value: 'unitPrice', sortable: false, align: 'end', width: '120px', order: getOrder('req_unit_price', 5) },
      { text: `Total${currSuffix}`, value: 'totalPrice', sortable: false, align: 'end', width: '130px', order: getOrder('req_total', 6) }
    ];

    // Add Note column if enabled (default to true for backward compatibility)
    if (this.isColumnEnabled('note')) {
      baseHeaders.push({ text: 'Note', value: 'note', sortable: false, align: 'start', width: '15%', order: getOrder('note', 3) });
    }

    // Tax columns (gated by column config)
    if (this.isColumnEnabled('tax_type')) {
      baseHeaders.push({ text: 'Tax Type', value: 'taxType', sortable: false, align: 'start', width: '140px', order: getOrder('tax_type', 7) });
    }
    if (this.isColumnEnabled('tax_rate')) {
      baseHeaders.push({ text: 'Tax %', value: 'taxRate', sortable: false, align: 'end', width: '100px', order: getOrder('tax_rate', 8) });
    }
    if (this.isColumnEnabled('tax_amount')) {
      baseHeaders.push({ text: `Tax Amount${currSuffix}`, value: 'taxAmount', sortable: false, align: 'end', width: '120px', order: getOrder('tax_amount', 9) });
    }
    if (this.isColumnEnabled('landed_cost')) {
      const landedCostLabel = this.isForeignCurrencyPO ? `Landed Cost (${this.storeCurrency})` : 'Landed Cost';
      baseHeaders.push({ text: landedCostLabel, value: 'landedCost', sortable: false, align: 'end', width: '120px', order: getOrder('landed_cost', 10) });
    }
    if (this.isColumnEnabled('landed_cost_shipping_per_unit')) {
      const shippingLabel = this.isForeignCurrencyPO ? `Shipping / Unit (${this.storeCurrency})` : 'Shipping / Unit';
      baseHeaders.push({ text: shippingLabel, value: 'landedCostShippingPerUnit', sortable: false, align: 'end', width: '120px', order: getOrder('landed_cost_shipping_per_unit', 11) });
    }
    if (this.isColumnEnabled('landed_cost_adjustment_per_unit')) {
      const adjLabel = this.isForeignCurrencyPO ? `Adjustment / Unit (${this.storeCurrency})` : 'Adjustment / Unit';
      baseHeaders.push({ text: adjLabel, value: 'landedCostAdjustmentPerUnit', sortable: false, align: 'end', width: '130px', order: getOrder('landed_cost_adjustment_per_unit', 12) });
    }
    if (this.isColumnEnabled('margin_amount')) {
      baseHeaders.push({ text: 'Margin', value: 'marginAmount', sortable: false, align: 'end', width: '120px', order: getOrder('margin_amount', 13) });
    }
    if (this.isColumnEnabled('margin_percent')) {
      baseHeaders.push({ text: 'Margin %', value: 'marginPercent', sortable: false, align: 'end', width: '100px', order: getOrder('margin_percent', 14) });
    }
    if (this.isColumnEnabled('landed_margin_amount')) {
      baseHeaders.push({ text: 'Landed Margin', value: 'landedMarginAmount', sortable: false, align: 'end', width: '120px', order: getOrder('landed_margin_amount', 15) });
    }
    if (this.isColumnEnabled('landed_margin_percent')) {
      baseHeaders.push({ text: 'Landed Margin %', value: 'landedMarginPercent', sortable: false, align: 'end', width: '120px', order: getOrder('landed_margin_percent', 16) });
    }

    const optionalHeaders: any[] = [];

    // Phase 1: Add optional columns from config (MasterData columns)
    // Note: PO item columns (received_quantity, returned_quantity, units_left) are displayed inside Quantity Status, not as separate columns
    if (this.columnConfig && this.columnConfig.columns) {
      Object.entries(this.columnConfig.columns).forEach(([columnId, config]) => {
        // Skip PO item columns - they are displayed inside the Quantity Status column, not as separate columns
        if (PO_ITEM_COLUMNS.includes(columnId)) return;
        if (config.enabled) {
          const metadata = COLUMN_METADATA[columnId];
          if (metadata) {
            optionalHeaders.push({
              text: metadata.label,
              value: columnId,
              sortable: false,
              align: NUMERIC_COLUMNS.includes(columnId) ? 'end' : 'start',
              order: config.order,
              type: 'masterdata'
            });
          }
        }
      });
    }

    // Phase 2: Add custom columns (order 40+)
    if (this.customColumnDefinitions) {
      Object.entries(this.customColumnDefinitions).forEach(([columnId, def]) => {
        if (def.enabled) {
          optionalHeaders.push({
            text: def.label,
            value: `custom_${columnId}`,
            sortable: false,
            align: 'start',
            order: def.order,
            type: 'custom',
            columnId: columnId
          });
        }
      });
    }

    // Phase 3: Add metafield columns
    if (this.metafieldColumns) {
      Object.entries(this.metafieldColumns).forEach(([key, config]) => {
        if (config.enabled) {
          optionalHeaders.push({
            text: config.label,
            value: `metafield_${key}`,
            sortable: false,
            align: 'start',
            order: config.order,
            type: 'metafield',
            metafieldKey: key
          });
        }
      });
    }

    // Combine and sort by order
    const allHeaders = [...baseHeaders, ...optionalHeaders];
    allHeaders.sort((a, b) => a.order - b.order);

    if (this.isEditing) {
      allHeaders.push({ text: '', value: 'actions', sortable: false, align: 'center', width: '50px', order: 999 });
    }

    return allHeaders;
  }

  // Total column count for export (required + optional + price columns based on toggle)
  // Base required columns: Image, Product, SKU, Quantity = 4
  // +2 for Unit Price and Total if exportIncludePricing is true
  private get totalExportColumnCount(): number {
    const PRICE_RELATED_COLUMNS = new Set([
      'unit_cost', 'selling_price', 'retail_value', 'actual_inventory_cost',
      'landed_cost', 'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit',
      'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'
    ]);
    const baseRequiredColumns = 4;
    let optionalCount = this.enabledOptionalColumns.length;
    if (!this.exportIncludePricing) {
      // Subtract price-related optional columns that the backend will strip
      optionalCount = this.enabledOptionalColumns.filter(
        (col: any) => !PRICE_RELATED_COLUMNS.has(col.id)
      ).length;
    }
    let count = baseRequiredColumns + optionalCount;
    if (this.exportIncludePricing) count += 2; // Unit Price + Total
    return count;
  }

  // Check if PDF export will be limited
  private get pdfColumnLimitWarning(): boolean {
    return this.totalExportColumnCount > 12;
  }

  // Phase 1, 2 & 3: Get list of enabled optional columns (MasterData + Custom + Metafield)
  private get enabledOptionalColumns() {
    const columns: any[] = [];

    // Phase 1: MasterData columns (skip required columns - they're counted separately)
    if (this.columnConfig && this.columnConfig.columns) {
      // Columns with explicit template slots and headers in baseHeaders
      // (margin columns have their own gated headers and color-coded template slots)
      const explicitSlotColumns = ['margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'];
      Object.entries(this.columnConfig.columns)
        .filter(([columnId, config]) => config.enabled && !columnId.startsWith('req_') && !explicitSlotColumns.includes(columnId))
        .forEach(([columnId, config]) => {
          columns.push({
            id: columnId,
            type: 'masterdata',
            order: config.order
          });
        });
    }

    // Phase 2: Custom columns
    if (this.customColumnDefinitions) {
      Object.entries(this.customColumnDefinitions)
        .filter(([_, def]) => def.enabled)
        .forEach(([columnId, def]) => {
          columns.push({
            id: `custom_${columnId}`,
            columnId: columnId,
            type: 'custom',
            order: def.order
          });
        });
    }

    // Phase 3: Metafield columns
    if (this.metafieldColumns) {
      Object.entries(this.metafieldColumns)
        .filter(([_, config]) => config.enabled)
        .forEach(([key, config]) => {
          columns.push({
            id: `metafield_${key}`,
            metafieldKey: key,
            type: 'metafield',
            label: config.label,
            order: config.order
          });
        });
    }

    return columns.sort((a, b) => a.order - b.order);
  }

  // Phase 1: Get MasterData value for a variant and column
  private getMasterDataValue(variantId: number | string, columnId: string, item?: any): string {
    // PO item columns (received_quantity, returned_quantity, units_left, note, landed_cost) come from the item, not MasterData
    if (PO_ITEM_COLUMNS.includes(columnId) && item) {
      // Special handling for calculated column: units_left = orderedQuantity - receivedQuantity
      if (columnId === 'units_left') {
        const ordered = Number(item.orderedQuantity || 0);
        const received = Number(item.receivedQuantity || 0);
        return String(ordered - received);
      }
      // Note column should return empty string for null/undefined, not '0'
      if (columnId === 'note') {
        const value = item.note;
        return value !== undefined && value !== null && value !== '' && value !== 0 && value !== '0'
          ? String(value)
          : '';
      }
      // Landed cost comes from item.extra.landed_cost
      if (columnId === 'landed_cost') {
        return item?.extra?.landed_cost != null ? this.formatCurrency(item.extra.landed_cost) : '';
      }
      if (columnId === 'landed_cost_shipping_per_unit') {
        const c = item?.extra?.landed_cost_components;
        if (!c || c.method !== 'proportional') return '';
        return c.shipping_per_unit != null ? this.formatCurrency(c.shipping_per_unit) : '';
      }
      if (columnId === 'landed_cost_adjustment_per_unit') {
        const c = item?.extra?.landed_cost_components;
        if (!c) return '';
        if (c.method === 'proportional') return c.adjustment_per_unit != null ? this.formatCurrency(c.adjustment_per_unit) : '';
        return '';
      }
      // Margin columns: computed from selling price (MasterData unit_price) and PO unitPrice / landed_cost
      // Use resolveSellingPrice() to handle comma-formatted strings from OLAP (e.g., "2,630.00")
      if (columnId === 'margin_amount' || columnId === 'margin_percent') {
        const variant = this.masterData[String(variantId)];
        const sellingPrice = this.resolveSellingPrice(variant);
        const cost = Number(item.unitPrice || 0);
        if (!sellingPrice) return '';
        if (columnId === 'margin_amount') return this.formatCurrency(sellingPrice - cost);
        const pct = ((sellingPrice - cost) / sellingPrice) * 100;
        return pct.toFixed(1) + '%';
      }
      if (columnId === 'landed_margin_amount' || columnId === 'landed_margin_percent') {
        const variant = this.masterData[String(variantId)];
        const sellingPrice = this.resolveSellingPrice(variant);
        const landedCost = item?.extra?.landed_cost;
        if (!sellingPrice || landedCost == null) return '';
        const cost = Number(landedCost);
        if (columnId === 'landed_margin_amount') return this.formatCurrency(sellingPrice - cost);
        const pct = ((sellingPrice - cost) / sellingPrice) * 100;
        return pct.toFixed(1) + '%';
      }
      const field = COLUMN_API_CODES[columnId];
      const value = item[field];
      // For numeric fields (received_quantity, returned_quantity), '0' is a valid value
      return value !== undefined && value !== null ? String(value) : '0';
    }

    // Convert to string for consistent lookup (masterData is indexed by string keys)
    const variantIdStr = String(variantId);
    const variant = this.masterData[variantIdStr];

    if (!variant) {
      return '';
    }

    // Look up by column ID directly (data is stored by column ID in fetchMasterData)
    // Fall back to API code lookup for backwards compatibility
    let value = variant[columnId];
    if (value === undefined) {
      const apiCode = COLUMN_API_CODES[columnId];
      if (apiCode) {
        value = variant[apiCode];
      }
    }

    // Return value or empty string (not undefined)
    return value !== undefined && value !== null ? String(value) : '';
  }

  // Check if a column should be right-aligned (numeric columns)
  private isNumericColumn(columnId: string): boolean {
    return NUMERIC_COLUMNS.includes(columnId);
  }

  // Get color class for margin percentage: green (>=30%), yellow (15-29%), red (<15%)
  private getMarginColorClass(variantId: number | string, cost: number | null | undefined, isLanded = false): string {
    const variant = this.masterData[String(variantId)];
    const sellingPrice = this.resolveSellingPrice(variant);
    if (!sellingPrice || cost == null) return '';
    const pct = ((sellingPrice - Number(cost)) / sellingPrice) * 100;
    if (pct >= 30) return 'success--text';
    if (pct >= 15) return 'warning--text';
    return 'error--text';
  }

  // Phase 2: Get custom column value for a variant
  private getCustomColumnValue(variantId: number | string, columnId: string): string {
    const variantIdStr = String(variantId);
    const values = this.customColumnValues[variantIdStr];
    if (!values) {
      return '';
    }

    const value = values[columnId];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return String(value);
  }

  // Phase 3: Get metafield value for a product
  // Accepts either productId directly or variantId (to look up productId from masterData)
  private getMetafieldValue(productIdOrVariantId: number | string, metafieldKey: string, isVariantId = false): string {
    let productIdStr: string;

    if (isVariantId) {
      // Look up product_id from masterData using variantId
      const variantIdStr = String(productIdOrVariantId);
      const masterDataItem = this.masterData[variantIdStr];
      if (!masterDataItem?.product_id) {
        return '';
      }
      productIdStr = String(masterDataItem.product_id);
    } else {
      productIdStr = String(productIdOrVariantId);
    }

    const values = this.metafieldValues[productIdStr];
    if (!values) {
      return '';
    }

    const value = values[metafieldKey];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return String(value);
  }

  @Watch('id', { immediate: true })
  onIdChange() {
    // Reset duplicate dialog state when PO changes
    this.showDuplicateDialog = false;
    this.duplicating = false;
    this.isEditingRate = false;
    this.editedRate = null;
    this.isSavingRate = false;
    this.isRefreshingRate = false;
    // Reset inline metadata edit state
    this.isEditingNotes = false;
    this.isEditingInternalNotes = false;
    this.isEditingExpectedDelivery = false;
    this.isEditingDeparture = false;

    this.fetchData();
    this.checkImportFlag();
    this.loadEmailPreferences();
    this.loadSavedEmails();
    this.loadExportPreferences();
  }

  @Watch('$route', { immediate: true })
  onRouteChange() {
    this.checkImportFlag();
  }

  @Watch('showReceiveDialog')
  onReceiveDialogChange(val: boolean) {
    if (val) {
      // Reset quantities when dialog opens
      this.initializeReceiveData();
      this.receiveNotes = '';
      this.receiveSearchQuery = '';
      // Reset mode and import-related state
      this.receiveMode = 'manual';
      this.importFile = null;
      this.importingReceive = false;
      this.downloadingTemplate = false;
      this.markAsCompleteAfterReceive = false;  // Reset mark as complete checkbox
      const savedMethod = localStorage.getItem('po_receive_cost_method');
      if (savedMethod && this.shopifyCostUpdateOptions.map(o => o.value).includes(savedMethod)) {
        this.shopifyCostUpdateMethod = savedMethod;
      } else {
        this.shopifyCostUpdateMethod = 'none';
      }
      // Initialize scroll indicator state
      this.showSettingsIndicator = !this.$vuetify.breakpoint.lgAndUp;
    } else {
      // Reset scroll indicator when dialog closes
      this.showSettingsIndicator = false;
    }
  }

  // Add watcher to handle mode switching and preserve manual mode state
  @Watch('receiveMode')
  onReceiveModeChange(newMode: 'manual' | 'import', oldMode: 'manual' | 'import') {
    if (newMode === 'import') {
      // When switching to import mode, reset import-related state
      this.importFile = null;
      this.importingReceive = false;
      this.downloadingTemplate = false;
    }
    // Note: When switching to manual mode, we preserve receiveQuantities, deliveryNoteNumber, and receiveNotes
    // This allows users to switch modes without losing their manual entry progress
  }

  @Watch('isEditing')
  async onEditingChange(val: boolean) {
    if (val) {
      // Fetch suppliers when entering edit mode
      await this.fetchSuppliers();
      // Fetch variant order constraints for validation
      await this.fetchVariantOrderConstraints();
    }
  }


  private async fetchData() {
    this.loading = true;
    // Reset error states
    this.purchaseOrderNotFound = false;
    this.hasError = false;
    this.errorMessage = '';
    
    try {
      this.purchaseOrder = await PurchaseOrderModel.getById(Number(this.id));
      if(this.purchaseOrder.locationId){
        this.purchaseOrder.locationId = Number(this.purchaseOrder.locationId);
      }
      this.items = this.purchaseOrder?.items || [];

      // Initialize edited purchase order with current data
      this.editedPurchaseOrder = {
        ...(this.purchaseOrder as IPurchaseOrder), // Cast to ensure all properties are available for spread
        expectedDeliveryDate: this.toDateInputValue(this.purchaseOrder?.expectedDeliveryDate) || undefined,
        supplierId: this.purchaseOrder?.supplierId, // Ensure supplierId is included
        supplierName: this.purchaseOrder?.supplierName // Include supplier name
      };

      // Initialize departure date and internal notes for edit mode
      this.editedDepartureDate = this.toDateInputValue(this.purchaseOrder?.departureDate);
      this.internalNotesEditValue = this.purchaseOrder?.extra?.internalNotes || '';

      // Set loading to false early so PO details are shown
      this.loading = false;

      // Fetch locations and column config first (needed to determine if MasterData is required)
      await Promise.all([
        this.fetchLocations(),
        this.fetchColumnConfig(),
        this.fetchCustomColumnDefinitions(),  // Phase 2
        this.taxTypeList.fetch().then(() => {
          // Prefer supplier's default tax type, fall back to merchant default
          const supplierDefaultId = this.supplier?.options?.financial?.default_tax_type_id;
          const supplierDefault = supplierDefaultId
            ? this.taxTypeList.items.find((t: TaxTypeModel) => t.id == supplierDefaultId)
            : null;
          const merchantDefault = this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault);
          const defaultType = supplierDefault || merchantDefault;
          if (defaultType) this.bulkTaxTypeId = defaultType.id;
        }).catch(() => {})  // Tax types for tax column dropdowns
      ]);

      // Check if we need MasterData (for enabled columns OR for metafield product_id lookup)
      const hasEnabledMasterDataColumns = this.columnConfig?.columns
        && Object.values(this.columnConfig.columns).some((config: any) => config.enabled);
      const hasEnabledMetafieldColumns = Object.values(this.metafieldColumns)
        .some((config: any) => config.enabled);

      // Fetch MasterData if needed for display OR for metafield product_id lookup
      if (hasEnabledMasterDataColumns || hasEnabledMetafieldColumns) {
        await this.fetchMasterData();
      }

      // Fetch custom column values after items are loaded (Phase 2)
      await this.fetchCustomColumnValues();

      // Fetch metafield values if there are enabled metafield columns (Phase 3)
      if (hasEnabledMetafieldColumns) {
        await this.fetchMetafieldValues();
      }

      // Fetch supplier details if there's a supplier
      if (this.purchaseOrder?.supplierId) {
        this.supplier = await SupplierModel.getById(this.purchaseOrder.supplierId);
        
        // Fetch variant order constraints for validation (in background for draft POs)
        if (this.purchaseOrder.status === 'draft') {
          this.fetchVariantOrderConstraints(); // Don't await, let it run in background
        }
        
        // Pre-populate email recipient with supplier email if available, without clobbering user edits
        if (this.supplier.contactEmail) {
          if (!this.emailRecipients || this.emailRecipients.length === 0) {
            this.emailRecipients = [this.supplier.contactEmail];
          }
        } else {
          this.emailRecipients = [];
        }

      }

      // Pre-populate email subject, without overwriting user-modified value
      if (!this.emailSubject) {
        this.emailSubject = `Purchase Order #${this.purchaseOrder.poNumber}`;
      }

      // Pre-populate company name with store name
      if (!this.companyName) {
        this.companyName = this.store.information?.name || '';
      }
      
      // Fetch location details if there's a location
      if (this.purchaseOrder.locationId) {
        this.location = this.locations.find(location => location.id === this.purchaseOrder?.locationId) || null;
      }

      // Fetch resolved addresses in background
      this.fetchResolvedAddresses();

      // Fetch audit history in background
      this.fetchAuditHistory();

      // Load return history in the background
      this.loadReturnHistory();
    } catch (error: any) {
      console.error('Error fetching purchase order:', error);
      
      // Check if it's a 404 error (purchase order not found)
      if (error?.response?.status === 404 || error?.status === 404) {
        this.purchaseOrderNotFound = true;
      } else {
        this.hasError = true;
        this.errorMessage = error?.message || 'Failed to load purchase order details';
        EventBus.$emit('show-snackbar', 'Failed to load purchase order details. ' + error.message, 'error');
      }
      
      this.loading = false;
    }
  }

  private async fetchLocations() {
    try {
      const locationsList = new StoreLocationList();
      await locationsList.fetch();
      this.locations = locationsList.items;
    } catch (error: any) {
      console.error('Error fetching locations:', error.message);
    }
  }

  // Phase 1: Fetch column configuration
  private async fetchColumnConfig() {
    this.loadingConfig = true;
    try {
      const response: any = await get('/purchase_orders/config/line-item-columns');
      this.columnConfig = response.data;
      // Phase 3: Load metafield columns config
      this.metafieldColumns = response.data?.metafieldColumns || {};
    } catch (error: any) {
      console.error('Failed to fetch column config:', error);
      // Fallback to default (no optional columns)
      this.columnConfig = { columns: {} };
      this.metafieldColumns = {};
    } finally {
      this.loadingConfig = false;
    }
  }

  // Phase 1: Fetch MasterData for variants in this PO
  private async fetchMasterData() {
    this.loadingMasterData = true;
    try {
      // Extract unique variant IDs from PO line items
      const variantIds = this.items
        .map((item: any) => String(item.variantId))
        .filter((id: string) => id && id !== 'null' && id !== 'undefined');

      if (variantIds.length === 0) {
        console.log('No variant IDs to fetch MasterData for');
        this.masterData = {};
        return;
      }

      // Build column_filter to fetch only these variants
      const columnFilter = [
        {
          code: 'variant_id',
          operator: 'in',
          values: variantIds,
          dataType: 'text'
        }
      ];

      // Build column list: required display columns + only ENABLED optional columns
      // Required columns for basic display (always needed)
      // variant_id is required for indexing the results
      // product_id is required for metafield lookups (Phase 3)
      const requiredDisplayColumns = ['variant_id', 'product_id', 'variant', 'product_sku', 'variant_image', 'unit_price'];

      // Get only ENABLED columns from columnConfig (not PO_ITEM_COLUMNS as they come from PO items)
      const enabledOptionalColumns: string[] = [];
      if (this.columnConfig?.columns) {
        Object.entries(this.columnConfig.columns).forEach(([columnId, config]: [string, any]) => {
          // Skip PO item columns (received_quantity, returned_quantity, units_left) - they come from PO items, not MasterData
          if (PO_ITEM_COLUMNS.includes(columnId)) return;
          // Only add enabled columns that have an API code mapping
          if (config.enabled && COLUMN_API_CODES[columnId]) {
            enabledOptionalColumns.push(COLUMN_API_CODES[columnId]);
          }
        });
      }

      // Combine and deduplicate (unit_price is already in requiredDisplayColumns, so margin columns work automatically)
      const allVariantColumns = [...new Set([...requiredDisplayColumns, ...enabledOptionalColumns])];

      // Fetch variants with filter and all columns
      const params = {
        column_filter: JSON.stringify(columnFilter),
        columns: allVariantColumns.join(','),
        limit: variantIds.length // Ensure we get all variants
      };

      const response: any = await get('/master-data/variants', params);

      // Index by variant_id for O(1) lookup
      this.masterData = {};

      // Master Data API returns OLAP format: { message, data: { columns, rows, ... } }
      // The columns array has metadata with the actual column codes
      const columns = response?.data?.columns || [];
      const rows = response?.data?.rows || [];

      if (Array.isArray(rows) && rows.length > 0 && Array.isArray(columns)) {
        rows.forEach((row: any) => {
          // Parse the OLAP row format - each row has items array
          const items = row.items || [];

          // Build a flat object from items array
          // Use the columns metadata to get the correct column code for each item
          const flatVariant: any = {};

          items.forEach((item: any, index: number) => {
            const value = item.value || '';
            const originalValue = item.original_value !== undefined && item.original_value !== null
              ? item.original_value : value;

            // Get the column metadata by index to know the correct code
            const columnMeta = columns[index];
            if (!columnMeta) return;

            const columnCode = columnMeta.code;

            // Store by column code (the unique identifier)
            flatVariant[columnCode] = value;
            // Store raw numeric value for calculations (avoids comma-formatted strings)
            flatVariant[`_raw_${columnCode}`] = originalValue;

            // Also store by internal column ID using reverse mapping
            // This allows getMasterDataValue to look up by column ID directly
            const columnId = COLUMN_ID_BY_API_CODE[columnCode];
            if (columnId) {
              flatVariant[columnId] = value;
              flatVariant[`_raw_${columnId}`] = originalValue;
            }

            // Legacy aliases for columns with different API codes
            if (columnCode === 'variant_unit_cost') {
              flatVariant.unit_cost = value;
            } else if (columnCode === 'inventoty_tracking') {
              // Typo in API response
              flatVariant.inventory_tracking = value;
            }
          });

          // Index by variant_id as string for O(1) lookup
          const variantId = String(flatVariant.variant_id || '');
          if (variantId) {
            this.masterData[variantId] = flatVariant;
          }
        });
      }

    } catch (error: any) {
      console.error('Failed to fetch MasterData:', error);
      this.masterData = {};
      // Don't show error to user - just log it
    } finally {
      this.loadingMasterData = false;
    }
  }

  // Phase 2: Fetch custom column definitions
  private async fetchCustomColumnDefinitions() {
    try {
      const response: any = await get('/purchase_orders/config/custom-columns');
      this.customColumnDefinitions = response.data?.columns || {};
    } catch (error: any) {
      console.error('Failed to fetch custom column definitions:', error);
      this.customColumnDefinitions = {};
    }
  }

  // Phase 2: Fetch custom column values for variants in this PO
  private async fetchCustomColumnValues() {
    this.loadingCustomColumns = true;
    try {
      // Extract unique variant IDs from PO line items
      const variantIds = this.items
        .map((item: any) => String(item.variantId))
        .filter((id: string) => id && id !== 'null' && id !== 'undefined');

      if (variantIds.length === 0) {
        this.customColumnValues = {};
        return;
      }

      const response: any = await get('/purchase_orders/config/custom-column-values', {
        object_ids: variantIds.join(','),
        object_type: 'variant'
      });

      // Build values map from response
      this.customColumnValues = response.data || {};
    } catch (error: any) {
      console.error('Failed to fetch custom column values:', error);
      this.customColumnValues = {};
    } finally {
      this.loadingCustomColumns = false;
    }
  }

  // Phase 3: Fetch metafield values for products in this PO
  private async fetchMetafieldValues() {
    // Get enabled metafield keys
    const enabledKeys = Object.entries(this.metafieldColumns)
      .filter(([_, config]) => config.enabled)
      .map(([key]) => key);

    // Skip if no metafield columns enabled
    if (enabledKeys.length === 0) {
      this.metafieldValues = {};
      return;
    }

    // Get unique product IDs from masterData (productId is in masterData, not directly on items)
    const productIds = [...new Set(
      this.items
        .map((item: any) => {
          // First try item.productId, then fallback to masterData
          if (item.productId) {
            return String(item.productId);
          }
          const variantIdStr = String(item.variantId);
          const masterDataItem = this.masterData[variantIdStr];
          return masterDataItem?.product_id ? String(masterDataItem.product_id) : null;
        })
        .filter(Boolean)
    )] as string[];

    // Skip if no products
    if (productIds.length === 0) {
      this.metafieldValues = {};
      return;
    }

    this.loadingMetafieldValues = true;

    try {
      const response: any = await get('/purchase_orders/config/metafield-values', {
        product_ids: productIds.join(','),
        keys: enabledKeys.join(',')
      });

      this.metafieldValues = response.data || {};
    } catch (error: any) {
      console.error('Failed to fetch metafield values:', error);
      this.metafieldValues = {};
    } finally {
      this.loadingMetafieldValues = false;
    }
  }

  private async fetchAuditHistory() {
    if (!this.purchaseOrder) return;

    this.loadingAuditHistory = true;
    try {
      this.auditHistory = await this.purchaseOrder.getHistory();
    } catch (error: any) {
      console.error('Error fetching audit history:', error.message);
      // Don't show error to user, just log it
    } finally {
      this.loadingAuditHistory = false;
    }
  }

  getEventColor(type: string): string {
    switch (type) {
      case 'status_change':
        return 'blue';
      case 'email_sent':
        return 'purple';
      case 'receive':
        return 'green';
      case 'return':
        return 'orange';
      default:
        return 'grey';
    }
  }

  getEventIcon(type: string): string {
    switch (type) {
      case 'status_change':
        return 'mdi-list-status';
      case 'email_sent':
        return 'mdi-email-send';
      case 'receive':
        return 'mdi-package-down';
      case 'return':
        return 'mdi-package-up';
      default:
        return 'mdi-information';
    }
  }

  getEventTitle(event: IPurchaseOrderHistoryItem): string {
    switch (event.type) {
      case 'status_change':
        return 'Status Changed';
      case 'email_sent':
        return 'Email Sent';
      case 'receive':
        return 'Items Received';
      case 'return':
        return 'Items Returned';
      default:
        return 'Event';
    }
  }

  getEventDescription(event: IPurchaseOrderHistoryItem): string {
    const data = event.data as any;
    switch (event.type) {
      case 'status_change':
        const prevStatus = this.formatStatus(data?.previousStatus);
        const newStatus = this.formatStatus(data?.newStatus);
        return `${prevStatus} → ${newStatus}`;
      case 'email_sent':
        const recipients = data?.recipients?.join(', ') || 'Unknown';
        const emailStatus = data?.status === 'sent' ? 'Sent to' : 'Failed to send to';
        return `${emailStatus} ${recipients}`;
      case 'receive':
        const receiveQty = data?.totalQuantity || 0;
        const receiveItems = data?.itemCount || 0;
        return `${receiveQty} units across ${receiveItems} items`;
      case 'return':
        const returnQty = data?.totalQuantity || 0;
        const returnItems = data?.itemCount || 0;
        return `${returnQty} units across ${returnItems} items`;
      default:
        return '';
    }
  }

  formatDateTime(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const supplierList = new SupplierList();
      await supplierList.fetch();
      this.suppliers = supplierList.items;
    } catch (error: any) {
      console.error('Error fetching suppliers:', error.message);
      EventBus.$emit('show-snackbar', 'Failed to load suppliers', 'error');
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchVariantOrderConstraints() {
    if (!this.purchaseOrder || !this.supplier) return;
    
    // Only fetch constraints for draft purchase orders
    if (this.purchaseOrder.status !== 'draft') return;
    
    this.loadingConstraints = true;
    try {
      // Get unique variant IDs from editable items (includes newly added items)
      const variantIds = [...new Set(this.items.map(item => item.variantId).filter(id => id > 0))];
      
      if (variantIds.length === 0) return;
      
      // Create filter for specific variants
      const filterColumns = new FilterColumnList();
      const variantFilter = new FilterColumnModel({
        name: 'Variant Id',
        code: 'variant_id',
        operator: 'eq',
        dataType: 'number',
        value: variantIds
      });
      filterColumns.add(variantFilter);
      
      // Fetch data using supplier's fetchProductsWithOptions
      const report = await this.supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        'eq',
        {
          initialFilterColumns: filterColumns,
          pageOptions: {
            page: 1,
            itemsPerPage: 1000  // Fetch all variants in one go
          },
          columns: [
            { code: 'variant_id', dataType: 'number', name: 'Variant ID' },
            { code: '[variant_min_order_quantity]', dataType: 'number', name: 'Min Order Qty' },
            { code: '[variant_max_order_quantity]', dataType: 'number', name: 'Max Order Qty' }
          ],
          suggestFutureReorderQuantity: true
        }
      );
      
      // Clear existing constraints
      this.variantOrderConstraints = {};
      
      // Parse report data using getReportRowsData() method
      const reportData = report.getReportRowsData();
      
      // Store constraints in map
      if (reportData && reportData.length > 0) {
        reportData.forEach((row: any) => {
          const variantId = row['variant_id'];
          const minQty = parseFloat(row['[variant_min_order_quantity]']) || 0;
          const maxQty = parseFloat(row['[variant_max_order_quantity]']) || 999999;
          
          if (variantId) {
            this.variantOrderConstraints[variantId] = {
              min: minQty,
              max: maxQty === 0 ? 999999 : maxQty  // Treat 0 max as no limit
            };
          }
        });
      }
      
      // Validate all items after loading constraints
      this.validateAllItems();
    } catch (error: any) {
      console.error('Error fetching variant order constraints:', error);
      // Don't show error to user, constraints are optional
    } finally {
      this.loadingConstraints = false;
    }
  }

  private async onSupplierChange(supplierId: number | null) {
    if (supplierId) {
      // Fetch the new supplier details
      try {
        const newSupplier = await SupplierModel.getById(supplierId);
        this.supplier = newSupplier;
        
        // Update the supplier name in the edited purchase order
        this.editedPurchaseOrder.supplierName = newSupplier.name;
        
        // Fetch variant constraints for the new supplier
        await this.fetchVariantOrderConstraints();
      } catch (error: any) {
        console.error('Error fetching supplier details:', error.message);
        EventBus.$emit('show-snackbar', 'Failed to load supplier details', 'error');
      }
    } else {
      // Clear supplier if null is selected
      this.supplier = null;
      this.editedPurchaseOrder.supplierName = undefined;
      this.variantOrderConstraints = {};
    }
  }

  private addItem() {
    this.items.push(
        {
            productName: '',
            sku: '',
            orderedQuantity: 1,
            receivedQuantity: 0,
            unitPrice: 0,
            totalPrice: 0,
            variantId: 0
        }
    );
  }

  private onProductsAdded(newItems: IPurchaseOrderItem[]) {
    if (newItems.length === 0) return;

    // Prepend new items to the top so users can easily edit qty, price, etc.
    this.items = [...newItems, ...this.items];

    // Enter edit mode if not already editing
    if (!this.isEditing) {
      this.isEditing = true;
    }

    // Refresh constraints so min/max warnings apply to newly added variants
    this.fetchVariantOrderConstraints();

    EventBus.$emit('show-snackbar', `${newItems.length} product(s) added to purchase order`, 'success');
  }

  private async removeItem(item: IPurchaseOrderItem) {
    if (!this.purchaseOrder || !item.id) return;

    this.deletingItemId = item.id;
    try {
      // Call the API to remove the item
      await this.purchaseOrder.removeItem(item.id);
      
      // Remove from local items array
      const index = this.items.findIndex(i => i.id === item.id);
      if (index !== -1) {
        this.items.splice(index, 1);
      }

      // Show success message
      EventBus.$emit('show-snackbar', 'Item removed successfully', 'success');
      
      // Update total amount
      if (this.purchaseOrder) {
        this.purchaseOrder.calculateTotalAmount();
      }
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to remove item: ${error.message}`, 'error');
      console.error('Error removing item:', error);
    } finally {
      this.deletingItemId = null;
    }
  }

  private updateItemTotal(item: IPurchaseOrderItem) {
    item.totalPrice = item.orderedQuantity * item.unitPrice;
    // Recalculate tax amount if a tax rate is set
    const taxRate = item.extra?.financial?.tax_rate || 0;
    if (taxRate > 0 && item.extra?.financial) {
      item.extra.financial.tax_amount = Math.round((item.orderedQuantity * item.unitPrice * taxRate) * 100) / 100;
    }
    // Validate quantity when it changes
    if (item.id) {
      this.validateItemQuantity(item);
    }
  }

  private validateItemQuantity(item: IPurchaseOrderItem) {
    if (!item.id) return;
    
    // Only validate when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') {
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) {
      // No constraints for this variant - use Vue.delete for reactivity
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const qty = item.orderedQuantity;
    
    if (qty < constraint.min) {
      Vue.set(this.quantityValidationErrors, item.id, `Minimum order quantity is ${constraint.min}`);
    } else if (qty > constraint.max && constraint.max < 999999) {
      Vue.set(this.quantityValidationErrors, item.id, `Maximum order quantity is ${constraint.max}`);
    } else {
      // Remove error - use Vue.delete for reactivity
      Vue.delete(this.quantityValidationErrors, item.id);
    }
  }

  private validateAllItems() {
    if (!this.items) return;
    
    // Clear all existing errors
    this.quantityValidationErrors = {};
    
    // Only validate items when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return;
    
    // Validate each item
    this.items.forEach(item => {
      if (item.id && item.variantId) {
        this.validateItemQuantity(item);
      }
    });
  }

  private getQuantityError(item: IPurchaseOrderItem): string {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return '';
    return item.id ? (this.quantityValidationErrors[item.id] || '') : '';
  }

  private getQuantityHint(item: IPurchaseOrderItem): string {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) return '';
    
    if (constraint.max >= 999999) {
      return constraint.min > 0 ? `Min: ${constraint.min}` : '';
    }
    
    return `Min: ${constraint.min}, Max: ${constraint.max}`;
  }

  private get hasQuantityErrors(): boolean {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return false;
    return Object.keys(this.quantityValidationErrors).length > 0;
  }

  private get quantityErrorItems(): IPurchaseOrderItem[] {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return [];
    return this.items.filter(item => 
      item.id && this.quantityValidationErrors[item.id]
    );
  }

  private get getSubtotal(): number {
    if (!this.items) return 0;
    return this.items.reduce((total, item) => {
      const itemTotal = Number(item.unitPrice || 0) * Number(item.orderedQuantity || 0);
      return total + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  }

  private get getTotalAmount(): number {
    if (!this.purchaseOrder) return this.getSubtotal;
    const subtotal = this.getSubtotal;
    const itemTaxTotal = this.getItemTaxTotal;
    const shippingCost = this.purchaseOrder.shippingCost;
    const shippingTaxAmount = this.purchaseOrder.shippingTaxAmount;
    const discountCalculated = this.purchaseOrder.discountCalculated;
    const adjustmentAmount = this.purchaseOrder.adjustmentAmount;
    return Math.round((subtotal + itemTaxTotal + shippingCost + shippingTaxAmount - discountCalculated + adjustmentAmount) * 100) / 100;
  }

  private get getTotalUnits(): number {
    if (!this.items) return 0;
    return this.items.reduce((total, item) => {
      const quantity = Number(item.orderedQuantity || 0);
      return total + (isNaN(quantity) ? 0 : quantity);
    }, 0);
  }

  private get getItemTaxTotal(): number {
    if (!this.items) return 0;
    return this.items.reduce((sum, item) => {
      return sum + (Number(item.extra?.financial?.tax_amount) || 0);
    }, 0);
  }

  private get getTotalTax(): number {
    const itemTax = this.getItemTaxTotal;
    const shippingTax = this.purchaseOrder?.shippingTaxAmount || 0;
    return itemTax + shippingTax;
  }

  private get landedCostRatio(): string {
    const serverRatio = this.purchaseOrder?.extra?.financial?.landed_cost_ratio;
    if (serverRatio != null) return Number(serverRatio).toFixed(2) + 'x';
    // Fallback for unsaved POs
    const subtotal = this.getSubtotal;
    if (subtotal <= 0) return '0.00x';
    const shipping = this.purchaseOrder?.shippingCost || 0;
    const adjustment = this.purchaseOrder?.adjustmentAmount || 0;
    const discount = this.purchaseOrder?.discountCalculated || 0;
    const discountedSubtotal = subtotal - discount;
    if (discountedSubtotal <= 0) return '0.00x';
    return ((discountedSubtotal + adjustment + shipping) / discountedSubtotal).toFixed(2) + 'x';
  }

  private get avgLandedCostPerUnit(): number {
    const serverAvg = this.purchaseOrder?.extra?.financial?.avg_landed_cost_per_unit;
    if (serverAvg != null) return Number(serverAvg);
    // Fallback for unsaved POs
    const units = this.getTotalUnits;
    if (units <= 0) return 0;
    const subtotal = this.getSubtotal;
    const shipping = this.purchaseOrder?.shippingCost || 0;
    const adjustment = this.purchaseOrder?.adjustmentAmount || 0;
    const discount = this.purchaseOrder?.discountCalculated || 0;
    return (subtotal - discount + adjustment + shipping) / units;
  }

  // Parse a numeric value that may be a comma-formatted string from OLAP (e.g., "2,630.00")
  private parseNumericValue(value: any): number {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    const cleaned = String(value).replace(/,/g, '');
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  }

  // Resolve selling price from masterData for a variant, handling comma-formatted OLAP strings
  private resolveSellingPrice(variant: any): number {
    if (!variant) return 0;
    const raw = variant._raw_selling_price ?? variant._raw_unit_price;
    if (raw != null) {
      const num = this.parseNumericValue(raw);
      if (num > 0) return num;
    }
    return this.parseNumericValue(variant.selling_price ?? variant.unit_price ?? 0);
  }

  // Profitability summary: helper to get raw selling price from masterData
  private getItemSellingPrice(item: any): number {
    const variant = this.masterData[String(item.variantId)];
    return this.resolveSellingPrice(variant);
  }

  // Count of items that have selling price data in masterData
  private get itemsWithSellingPrice(): number {
    return this.items.filter(item => this.getItemSellingPrice(item) > 0).length;
  }

  // Total revenue from selling prices (only items with selling price data)
  private get totalRevenue(): number {
    return this.items.reduce((sum, item) => {
      const price = this.getItemSellingPrice(item);
      if (!price) return sum;
      return sum + price * Number(item.orderedQuantity || 0);
    }, 0);
  }

  // Total margin = total revenue - total cost (for items with selling price)
  private get totalMargin(): number {
    return this.items.reduce((sum, item) => {
      const price = this.getItemSellingPrice(item);
      if (!price) return sum;
      const qty = Number(item.orderedQuantity || 0);
      return sum + (price * qty) - (Number(item.unitPrice || 0) * qty);
    }, 0);
  }

  // Average margin % = (totalMargin / totalRevenue) × 100
  private get avgMarginPercent(): number {
    if (this.totalRevenue <= 0) return 0;
    return (this.totalMargin / this.totalRevenue) * 100;
  }

  // Landed margin aggregation (only items with BOTH selling price AND landed_cost)
  private get totalLandedMargin(): { margin: number; percent: number; count: number } {
    let revenue = 0;
    let cost = 0;
    let count = 0;
    this.items.forEach((item: any) => {
      const price = this.getItemSellingPrice(item);
      const lc = item.extra?.landed_cost;
      if (!price || lc == null) return;
      const qty = Number(item.orderedQuantity || 0);
      revenue += price * qty;
      cost += Number(lc) * qty;
      count++;
    });
    const margin = revenue - cost;
    const percent = revenue > 0 ? (margin / revenue) * 100 : 0;
    return { margin, percent, count };
  }

  // Color class for margin percentage values (summary-level)
  private getMarginPercentColorClass(percent: number): string {
    if (percent >= 30) return 'success--text';
    if (percent >= 15) return 'warning--text';
    return 'error--text';
  }

  private onTaxTypeChange(item: IPurchaseOrderItem, taxTypeId: number | null) {
    if (!item.extra) Vue.set(item, 'extra', {});
    if (!item.extra!.financial) Vue.set(item.extra!, 'financial', {});

    if (!taxTypeId) {
      // Cleared selection
      Vue.set(item.extra!.financial!, 'tax_type_id', undefined);
      Vue.set(item.extra!.financial!, 'tax_type_name', undefined);
      Vue.set(item.extra!.financial!, 'tax_rate', 0);
      Vue.set(item.extra!.financial!, 'tax_amount', 0);
      return;
    }

    const taxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id === taxTypeId);
    if (!taxType) return;

    Vue.set(item.extra!.financial!, 'tax_type_id', taxType.id);
    Vue.set(item.extra!.financial!, 'tax_type_name', taxType.name);
    Vue.set(item.extra!.financial!, 'tax_rate', taxType.rate);
    const taxAmount = Math.round((item.orderedQuantity * item.unitPrice * taxType.rate) * 100) / 100;
    Vue.set(item.extra!.financial!, 'tax_amount', taxAmount);
  }

  private onTaxRateInput(item: IPurchaseOrderItem, value: string) {
    if (!item.extra) Vue.set(item, 'extra', {});
    if (!item.extra!.financial) Vue.set(item.extra!, 'financial', {});

    const pct = Number(value) || 0;
    const rate = pct / 100;
    Vue.set(item.extra!.financial!, 'tax_rate', rate);
    Vue.set(item.extra!.financial!, 'tax_type_id', undefined);
    Vue.set(item.extra!.financial!, 'tax_type_name', rate > 0 ? 'Custom' : undefined);
    const taxAmount = Math.round((item.orderedQuantity * item.unitPrice * rate) * 100) / 100;
    Vue.set(item.extra!.financial!, 'tax_amount', taxAmount);
  }

  private getLineTotalWithTax(item: IPurchaseOrderItem): number {
    const lineTotal = (Number(item.orderedQuantity) || 0) * (Number(item.unitPrice) || 0);
    const taxAmount = Number(item.extra?.financial?.tax_amount) || 0;
    return lineTotal + taxAmount;
  }

  private get hasTaxColumnsEnabled(): boolean {
    return this.isColumnEnabled('tax_type') || this.isColumnEnabled('tax_rate') || this.isColumnEnabled('tax_amount');
  }

  private applyTaxToAllItems() {
    if (!this.bulkTaxTypeId) return;
    const taxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id === this.bulkTaxTypeId);
    if (!taxType) return;

    for (const item of this.items) {
      this.onTaxTypeChange(item, taxType.id);
    }
    this.showBulkTaxMenu = false;
  }

  private clearTaxFromAllItems() {
    for (const item of this.items) {
      this.onTaxTypeChange(item, null);
    }
    this.showBulkTaxMenu = false;
  }

  // Return computed properties
  get canReturnItems(): boolean {
    // Can return items from partial_received, received, or completed POs
    // AND there must be items with receivable quantity > returned quantity
    if (!this.purchaseOrder?.items) return false;
    const statusAllowsReturn =
      this.purchaseOrder.status === 'partial_received' ||
      this.purchaseOrder.status === 'received' ||
      this.purchaseOrder.status === 'completed';
    const hasReturnableItems = this.purchaseOrder.items.some(item =>
      (item.receivedQuantity || 0) > (item.returnedQuantity || 0)
    );
    return statusAllowsReturn && hasReturnableItems;
  }

  get returnableItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];
    return this.purchaseOrder.items.filter(item =>
      (item.receivedQuantity || 0) > (item.returnedQuantity || 0)
    );
  }

  get returnTableHeaders() {
    return [
      { text: 'Product', value: 'product', sortable: false },
      { text: 'Received', value: 'receivedQuantity', align: 'center' },
      { text: 'Returned', value: 'returnedQuantity', align: 'center' },
      { text: 'Available', value: 'availableToReturn', align: 'center' },
      { text: 'Return Qty', value: 'returnQuantity', align: 'center', sortable: false }
    ];
  }

  get totalReturningItems(): number {
    return this.returnableItems.filter(item =>
      this.returnQuantities[item.id!] && this.returnQuantities[item.id!] > 0
    ).length;
  }

  get totalReturningQuantity(): number {
    return this.returnableItems.reduce((sum, item) => {
      const qty = this.returnQuantities[item.id!] || 0;
      return sum + Number(qty);
    }, 0);
  }

  get totalReturningValue(): number {
    return this.returnableItems.reduce((sum, item) => {
      const qty = this.returnQuantities[item.id!] || 0;
      const cost = item.unitPrice || 0;
      return sum + (Number(qty) * cost);
    }, 0);
  }

  get canSubmitReturn(): boolean {
    const hasItems = this.totalReturningItems > 0 && this.totalReturningQuantity > 0;
    const hasErrors = Object.keys(this.returnQuantityErrors).length > 0;
    return hasItems && !hasErrors;
  }

  get sortedReturnHistory(): any[] {
    return [...this.returnHistory].sort((a, b) => {
      const dateA = new Date(a.returnDate).getTime();
      const dateB = new Date(b.returnDate).getTime();
      return dateB - dateA;
    });
  }

  private async savePurchaseOrder() {
    if (!this.purchaseOrder) return;

    this.saving = true;
    try {
      // For non-draft POs, only update extra field (departure date)
      if (this.purchaseOrder.status !== 'draft') {
        // Update departure date and internal notes (stored in extra field)
        this.purchaseOrder.departureDate = this.editedDepartureDate || undefined;
        if (!this.purchaseOrder.extra) this.purchaseOrder.extra = {};
        this.purchaseOrder.extra.internalNotes = this.internalNotesEditValue?.trim() || undefined;
        await put(`/purchase_orders/${this.purchaseOrder.id}`, {
          extra: this.purchaseOrder.extra
        });
      } else {
        // For draft POs, update all fields
        Object.assign(this.purchaseOrder, this.editedPurchaseOrder);
        this.purchaseOrder.items = this.items;

        // Update departure date and internal notes AFTER Object.assign to avoid being overwritten
        this.purchaseOrder.departureDate = this.editedDepartureDate || undefined;
        if (!this.purchaseOrder.extra) this.purchaseOrder.extra = {};
        this.purchaseOrder.extra.internalNotes = this.internalNotesEditValue?.trim() || undefined;

        // Calculate totals
        this.purchaseOrder.calculateTotalAmount();

        // Save to API
        await this.purchaseOrder.save();

        // Re-sync local items from model (server may have updated extra fields)
        this.items = this.purchaseOrder.items;
      }

      this.isEditing = false;
      EventBus.$emit('show-snackbar', 'Purchase order updated successfully', 'success');
      
      // Update location if needed
      if (this.purchaseOrder.locationId) {
        this.location = this.locations.find(loc => loc.id === this.purchaseOrder?.locationId) || null;
      } else {
        this.location = null;
      }
      
      // Update supplier if changed
      if (this.editedPurchaseOrder.supplierId !== undefined && this.purchaseOrder.supplierId !== this.editedPurchaseOrder.supplierId) {
        if (this.editedPurchaseOrder.supplierId) {
          this.supplier = await SupplierModel.getById(this.editedPurchaseOrder.supplierId);
        } else {
          this.supplier = null;
        }
      }
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to update purchase order', 'error');
      console.error('Error updating purchase order:', error);
    } finally {
      this.saving = false;
    }
  }

  private async fetchResolvedAddresses() {
    if (!this.purchaseOrder?.id) return;
    try {
      const response: any = await get(`/purchase_orders/${this.purchaseOrder.id}/addresses`);
      this.resolvedBillTo = response.billTo || null;
      this.billToSource = response.billToSource || 'default';
      this.resolvedShipTo = response.shipTo || null;
      this.shipToSource = response.shipToSource || 'default';
    } catch (error: any) {
      console.warn('Failed to fetch resolved addresses:', error.message);
    }
  }

  private openAddressDialog(type: 'bill_to' | 'ship_to') {
    if (!this.purchaseOrder) return;
    this.editingAddressType = type;
    const resolved = type === 'bill_to' ? this.resolvedBillTo : this.resolvedShipTo;
    this.editedAddress = resolved ? { ...resolved } : { name: '', address1: '', address2: '', city: '', province: '', zip: '', country: '', phone: '' };
    this.showAddressDialog = true;
    this.fetchAddressSuggestions(type);
  }

  private async fetchAddressSuggestions(type: 'bill_to' | 'ship_to') {
    this.addressSuggestions = [];
    this.loadingAddressSuggestions = true;
    try {
      const response: any = await get(`/purchase_orders/addresses/suggestions?type=${type}`);
      this.addressSuggestions = Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.warn('Failed to fetch address suggestions:', error.message);
    } finally {
      this.loadingAddressSuggestions = false;
    }
  }

  private onAddressSuggestionSelected(addr: Record<string, string> | null) {
    if (!addr) return;
    this.editedAddress = {
      name: addr.name || '',
      address1: addr.address1 || '',
      address2: addr.address2 || '',
      city: addr.city || '',
      province: addr.province || '',
      zip: addr.zip || '',
      country: addr.country || '',
      phone: addr.phone || '',
    };
  }

  private async saveAddress() {
    if (!this.purchaseOrder) return;
    this.savingAddress = true;
    try {
      const extra = {
        ...this.purchaseOrder.extra,
        [this.editingAddressType]: { ...this.editedAddress }
      };
      await put(`/purchase_orders/${this.purchaseOrder.id}`, { extra });
      this.purchaseOrder.extra = extra;
      this.showAddressDialog = false;
      await this.fetchResolvedAddresses();
      EventBus.$emit('show-snackbar', `${this.editingAddressType === 'bill_to' ? 'Bill To' : 'Ship To'} updated successfully`, 'success');
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to save address', 'error');
      console.error('Error saving address:', error);
    } finally {
      this.savingAddress = false;
    }
  }

  private clearAddress(type: 'bill_to' | 'ship_to') {
    this.resetAddressType = type;
    this.showResetAddressDialog = true;
  }

  private async confirmClearAddress() {
    if (!this.purchaseOrder) return;
    this.resettingAddress = true;
    try {
      const extra = {
        ...this.purchaseOrder.extra,
        [this.resetAddressType]: null
      };
      await put(`/purchase_orders/${this.purchaseOrder.id}`, { extra });
      this.purchaseOrder.extra = extra;
      this.showResetAddressDialog = false;
      await this.fetchResolvedAddresses();
      const label = this.resetAddressType === 'bill_to' ? 'Bill To' : 'Ship To';
      EventBus.$emit('show-snackbar', `${label} cleared — will use default`, 'success');
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to clear address', 'error');
      console.error('Error clearing address:', error);
    } finally {
      this.resettingAddress = false;
    }
  }

  private onFinancialSaved(data: { totalAmount: number; extra: any; items?: any[] }) {
    if (!this.purchaseOrder) return;
    this.purchaseOrder.totalAmount = data.totalAmount;
    this.purchaseOrder.extra = data.extra;
    if (data.items) {
      this.items = data.items;
      this.purchaseOrder.items = data.items;
    }
  }

  private cancelEdit() {
    this.isEditing = false;
    this.fetchData(); // Reset to original data
  }

  private async confirmSaveChanges() {
    await this.savePurchaseOrder();
    this.showSaveChangesDialog = false;
  }

  private confirmDiscardChanges() {
    this.showDiscardChangesDialog = false;
    this.cancelEdit();
  }

  private async loadImageAsBase64(url: string): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/jpeg');
          resolve(dataURL);
        } else {
          resolve(null);
        }
      };
      img.onerror = function() {
        resolve(null);
      };
      img.src = url;
    });
  }

  private validateEmailForm() {
    if (!this.$refs.emailForm || !this.purchaseOrder) return;

    const form = this.$refs.emailForm as Vue & { validate(): boolean };
    return form.validate();
  }

  private async sendEmail() {
    if (!this.$refs.emailForm || !this.purchaseOrder) return;

    // Validate form
    const form = this.$refs.emailForm as Vue & { validate(): boolean };
    if (!form.validate()) return;

    this.sending = true;
    try {
      // Save preferences before sending
      this.saveEmailPreferences();
      this.saveSavedEmails();

      await this.purchaseOrder.sendToSupplier({
        emailSubject: this.emailSubject,
        recipients: this.emailRecipients,
        customMessage: this.emailMessage,
        attachmentFormat: this.attachmentFormat,
        includePricing: this.includePricing,
        replyTo: this.replyToEmails.length > 0 ? this.replyToEmails : undefined,
        companyName: this.companyName || undefined
      });
      
      this.showEmailDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order sent successfully!', 'success');
      
      // Refresh data to get updated status
      await this.fetchData();
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to send purchase order', 'error');
      console.error('Error sending purchase order:', error);
    } finally {
      this.sending = false;
    }
  }

    private async exportPurchaseOrder(): Promise<void> {
    if (!this.purchaseOrder) return;

    this.exporting = true;

    try {
      const token = getIdentityToken();

      // Prepare headers conditionally
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Get enabled optional columns
      const enabledColumns: string[] = [];
      if (this.columnConfig && this.columnConfig.columns) {
        Object.entries(this.columnConfig.columns).forEach(([columnId, config]) => {
          if (config.enabled) {
            enabledColumns.push(columnId);
          }
        });
      }

      // Make authenticated GET request to the new export endpoint
      const response = await ax.get(`/purchase_orders/${this.purchaseOrder.id}/export`, {
        params: {
          format: this.exportFormat,
          includePricing: this.exportIncludePricing,
          optionalColumns: enabledColumns.length > 0 ? enabledColumns.join(',') : undefined
        },
        responseType: 'blob',
        headers
      });

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `PO-${this.purchaseOrder.poNumber}.${this.exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);

      // Close dialog
      this.showExportDialog = false;

      // Show success message
      EventBus.$emit('show-snackbar', 'Export completed successfully.', 'success');
    } catch (error: any) {
      console.error('Export error:', error);
      
      // Handle error message extraction from blob response
      let errorMessage = 'Export failed. Please try again.';
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If parsing fails, use default message
          console.error('Failed to parse error response:', parseError);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.exporting = false;
    }
  }


  private removeRecipient(index: number) {
    this.emailRecipients.splice(index, 1);
  }

  private removeReplyToEmail(index: number) {
    this.replyToEmails.splice(index, 1);
  }

  private validateEmail(email: string): boolean {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  get canEditPurchaseOrder(): boolean {
    if (!this.purchaseOrder) return false;

    // Only allow editing if status is draft
    return this.purchaseOrder.status === 'draft';
  }

  get canEditAddresses(): boolean {
    if (!this.purchaseOrder) return false;
    return ['draft', 'pending_approval'].includes(this.purchaseOrder.status);
  }

  get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'purchase_order',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'grey';

    switch (status) {
      case 'draft':
        return 'grey';
      case 'confirmed':
        return 'purple';
      case 'partial_received':
        return 'orange';
      case 'received':
        return 'green';
      case 'completed':
        return 'teal';
      case 'canceled':
        return 'red';
      default:
        return 'grey';
    }
  }

  getStatusIcon(status: string | undefined): string {
    if (!status) return 'mdi-help-circle-outline';

    switch (status) {
      case 'draft':
        return 'mdi-file-document-edit-outline';
      case 'confirmed':
        return 'mdi-check-circle-outline';
      case 'partial_received':
        return 'mdi-package-variant';
      case 'received':
        return 'mdi-package-check';
      case 'completed':
        return 'mdi-check-circle';
      case 'canceled':
        return 'mdi-cancel';
      default:
        return 'mdi-help-circle-outline';
    }
  }

  formatStatus(status: string | undefined): string {
    if (!status) return '';

    switch (status) {
      case 'draft':
        return 'Draft';
      case 'confirmed':
        return 'Confirmed';
      case 'partial_received':
        return 'Partially Received';
      case 'received':
        return 'Received';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Add ordinal suffix to day
    const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3 ? 0 : day % 10)] || 'th';
    
    return `${month} ${day}${suffix}, ${year}`;
  }

  formatCurrency(value: number | undefined): string {
    return CurrencyHelper.formatCurrency(value);
  }

  formatCurrencyFor(value: number | undefined | null): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.poCurrencyCode);
  }

  formatCurrencyStore(value: number | undefined | null): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.storeCurrency);
  }

  getCurrencySymbol(code: string): string {
    return CurrencyHelper.getCurrencySymbol(code);
  }

  get receiveCostPrefix(): string {
    return CurrencyHelper.getCurrencySymbol(this.storeCurrency);
  }

  getLineTotalBase(item: any): number | null {
    const unitPriceBase = item.extra?.unit_price_base;
    if (unitPriceBase == null) return null;
    const taxBase = item.extra?.tax_amount_base || 0;
    return (unitPriceBase * (item.orderedQuantity || 0)) + taxBase;
  }

  get storeCurrency(): string {
    return CurrencyHelper.getStoreCurrency();
  }

  get isForeignCurrencyPO(): boolean {
    return this.purchaseOrder?.isForeignCurrency ?? false;
  }

  get poCurrencyCode(): string {
    return this.purchaseOrder?.currencyCode || this.storeCurrency;
  }

  isRefreshingRate = false;
  isEditingRate = false;
  editedRate: number | null = null;
  isSavingRate = false;

  isEditingNotes = false;
  editingNotesValue = '';
  savingNotes = false;
  isEditingInternalNotes = false;
  editingInternalNotesValue = '';
  savingInternalNotes = false;
  isEditingExpectedDelivery = false;
  editingExpectedDeliveryValue = '';
  savingExpectedDelivery = false;
  showExpectedDeliveryPicker = false;
  isEditingDeparture = false;
  editingDepartureValue = '';
  savingDeparture = false;
  showDeparturePicker = false;

  get isExchangeRateTerminal(): boolean {
    if (!this.purchaseOrder?.currencyCode) return true;
    return this.purchaseOrder.isTerminalStatus();
  }

  get canEditExchangeRate(): boolean {
    return !this.isExchangeRateTerminal && !this.purchaseOrder?.exchangeRateLocked;
  }

  get canInlineEditMetadata(): boolean {
    if (!this.purchaseOrder) return false;
    if (this.isEditing) return false;
    return this.purchaseOrder.canEditMetadata();
  }

  private ensureFinancial(): IPurchaseOrderFinancial {
    if (!this.purchaseOrder!.extra) this.purchaseOrder!.extra = {};
    if (!this.purchaseOrder!.extra.financial) this.purchaseOrder!.extra.financial = {};
    return this.purchaseOrder!.extra.financial;
  }

  // Targeted PUT for extra-only updates — save() sends the full PO which gets rejected for non-draft statuses
  private async saveExtraToServer(): Promise<void> {
    const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
      extra: this.purchaseOrder!.extra
    });
    if (response?.data) {
      this.purchaseOrder!.mapData(response.data);
      this.items = this.purchaseOrder!.items || [];
    }
  }

  private snapshotFinancial(): any {
    return JSON.parse(JSON.stringify(this.purchaseOrder?.extra?.financial || {}));
  }

  private restoreFinancial(snapshot: any) {
    if (this.purchaseOrder?.extra) {
      this.purchaseOrder.extra.financial = snapshot;
    }
  }

  async onExchangeRateChange(payload: { exchange_rate: number; exchange_rate_manual: boolean }) {
    if (!this.purchaseOrder) return;
    const snapshot = this.snapshotFinancial();
    const f = this.ensureFinancial();
    f.exchange_rate = payload.exchange_rate;
    f.exchange_rate_manual = payload.exchange_rate_manual;
    f.exchange_rate_source = null;
    f.exchange_rate_fetched_at = null;
    try {
      await this.saveExtraToServer();
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error saving exchange rate:', err);
      throw err;
    }
  }

  startEditingRate() {
    this.editedRate = Number(this.purchaseOrder?.exchangeRate) || null;
    this.isEditingRate = true;
  }

  cancelEditingRate() {
    this.isEditingRate = false;
    this.editedRate = null;
  }

  async saveEditedRate() {
    const num = Number(this.editedRate);
    if (!num || num <= 0 || !isFinite(num)) {
      EventBus.$emit('show-snackbar', { message: 'Please enter a valid exchange rate greater than zero', color: 'warning' });
      return;
    }
    this.isSavingRate = true;
    try {
      await this.onExchangeRateChange({ exchange_rate: num, exchange_rate_manual: true });
      this.isEditingRate = false;
      this.editedRate = null;
    } catch (err) {
      console.error('Error saving exchange rate:', err);
      EventBus.$emit('show-snackbar', { message: 'Failed to update exchange rate', color: 'error' });
    } finally {
      this.isSavingRate = false;
    }
  }

  private toDateInputValue(raw: string | undefined | null): string {
    return raw ? new Date(raw).toISOString().split('T')[0] : '';
  }

  startEditingNotes() {
    this.editingNotesValue = this.purchaseOrder?.notes || '';
    this.isEditingNotes = true;
  }

  cancelEditingNotes() {
    this.isEditingNotes = false;
  }

  async saveInlineNotes() {
    this.savingNotes = true;
    const previousValue = this.purchaseOrder!.notes;
    try {
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
        notes: this.editingNotesValue
      });
      if (response?.data) {
        this.purchaseOrder!.notes = response.data.notes;
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      this.isEditingNotes = false;
    } catch (e: any) {
      this.purchaseOrder!.notes = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save notes',
        color: 'error'
      });
    } finally {
      this.savingNotes = false;
    }
  }

  startEditingInternalNotes() {
    this.editingInternalNotesValue = this.purchaseOrder?.extra?.internalNotes || '';
    this.isEditingInternalNotes = true;
  }

  cancelEditingInternalNotes() {
    this.isEditingInternalNotes = false;
    this.editingInternalNotesValue = '';
  }

  async saveInlineInternalNotes() {
    this.savingInternalNotes = true;
    try {
      const extra = {
        ...(this.purchaseOrder!.extra || {}),
        internalNotes: this.editingInternalNotesValue?.trim() || undefined,
      };
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, { extra });
      this.purchaseOrder!.extra = extra;
      if (response?.data?.updatedAt) {
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      // Sync the draft edit buffer so a subsequent page-level save doesn't overwrite with stale data
      this.internalNotesEditValue = this.purchaseOrder?.extra?.internalNotes || '';
      this.isEditingInternalNotes = false;
    } catch (e: any) {
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save internal notes',
        color: 'error'
      });
    } finally {
      this.savingInternalNotes = false;
    }
  }

  startEditingExpectedDelivery() {
    this.editingExpectedDeliveryValue = this.toDateInputValue(this.purchaseOrder?.expectedDeliveryDate);
    this.isEditingExpectedDelivery = true;
  }

  cancelEditingExpectedDelivery() {
    this.isEditingExpectedDelivery = false;
    this.showExpectedDeliveryPicker = false;
  }

  async saveInlineExpectedDelivery() {
    this.savingExpectedDelivery = true;
    const previousValue = this.purchaseOrder!.expectedDeliveryDate;
    try {
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
        expectedDeliveryDate: this.editingExpectedDeliveryValue || null
      });
      if (response?.data) {
        this.purchaseOrder!.expectedDeliveryDate = response.data.expectedDeliveryDate;
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      this.isEditingExpectedDelivery = false;
      this.showExpectedDeliveryPicker = false;
    } catch (e: any) {
      this.purchaseOrder!.expectedDeliveryDate = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save expected delivery date',
        color: 'error'
      });
    } finally {
      this.savingExpectedDelivery = false;
    }
  }

  startEditingDeparture() {
    this.editingDepartureValue = this.toDateInputValue(this.purchaseOrder?.departureDate);
    this.isEditingDeparture = true;
  }

  cancelEditingDeparture() {
    this.isEditingDeparture = false;
    this.showDeparturePicker = false;
  }

  async saveInlineDeparture() {
    this.savingDeparture = true;
    const previousValue = this.purchaseOrder!.departureDate;
    try {
      this.purchaseOrder!.departureDate = this.editingDepartureValue || undefined;
      await this.saveExtraToServer();
      this.isEditingDeparture = false;
      this.showDeparturePicker = false;
    } catch (e: any) {
      this.purchaseOrder!.departureDate = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save departure date',
        color: 'error'
      });
    } finally {
      this.savingDeparture = false;
    }
  }

  async onRefreshExchangeRate() {
    if (!this.purchaseOrder?.currencyCode) return;
    this.isRefreshingRate = true;
    const snapshot = this.snapshotFinancial();
    try {
      const response: any = await get(`/exchange-rates?from=${this.purchaseOrder.currencyCode}&to=${this.storeCurrency}&refresh=true`);
      if (response?.rate) {
        const f = this.ensureFinancial();
        f.exchange_rate = response.rate;
        f.exchange_rate_source = response.from ? `${response.from}→${response.to}` : null;
        f.exchange_rate_fetched_at = response.timestamp || new Date().toISOString();
        f.exchange_rate_manual = false;
        await this.saveExtraToServer();
      }
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error refreshing exchange rate:', err);
    } finally {
      this.isRefreshingRate = false;
    }
  }

  async onUnlockExchangeRate() {
    if (!this.purchaseOrder) return;
    const snapshot = this.snapshotFinancial();
    const f = this.ensureFinancial();
    f.exchange_rate_locked = false;
    f.exchange_rate_locked_at = null;
    try {
      await this.saveExtraToServer();
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error unlocking exchange rate:', err);
    }
  }

  // NEW: Helper methods for cost status display in import summary
  getCostUpdateStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'updated': 'success',
      'partial': 'warning', 
      'failed': 'error',
      'skipped': 'grey'
    };
    return colorMap[status] || 'grey';
  }

  getCostUpdateStatusText(status: string): string {
    const textMap: Record<string, string> = {
      'updated': 'Updated',
      'partial': 'Partially Updated',
      'failed': 'Failed', 
      'skipped': 'Skipped'
    };
    return textMap[status] || 'Unknown';
  }

  getCostMethodText(method: string): string {
    const methodMap: Record<string, string> = {
      'latest': 'Latest Cost',
      'average': 'Weighted Average'
    };
    return methodMap[method] || method;
  }

  get canReceiveItems(): boolean {
    // Can only receive items before fully received (use Return Items for received POs)
    return (
      this.purchaseOrder?.status === 'confirmed' ||
      this.purchaseOrder?.status === 'partial_received'
    );
  }

  get canMarkAsCompleted(): boolean {
    // Can only complete after receiving has started
    return (
      this.purchaseOrder?.status === 'partial_received' ||
      this.purchaseOrder?.status === 'received'
    );
  }

  get canCancelPO(): boolean {
    // Can only cancel before receiving starts
    return (
      this.purchaseOrder?.status === 'draft' ||
      this.purchaseOrder?.status === 'confirmed'
    );
  }

  get canRevertToDraft(): boolean {
    // Can only revert to draft from confirmed status (before receiving starts)
    return this.purchaseOrder?.status === 'confirmed';
  }

  get hasStatusActions(): boolean {
    // Always true since Duplicate PO is always available
    return true;
  }

  get hasSecondaryActions(): boolean {
    // Check if there are secondary actions (shown in "More" menu)
    return (
      this.canAddProducts ||
      this.canImportItems ||
      this.moreMenuButtons.length > 0
    );
  }

  // Count of status-dependent action buttons (including Duplicate which is always available)
  get actionButtonCount(): number {
    let count = 1; // Duplicate PO is always available
    if (this.canConfirmPurchaseOrder) count++;
    if (this.canEditPurchaseOrder && !this.isEditing) count++;
    if (this.canReceiveItems) count++;
    if (this.canReturnItems) count++;
    if (this.canMarkAsCompleted) count++;
    if (this.canRevertToDraft) count++;
    if (this.canCancelPO) count++;
    return count;
  }

  // Should we collapse buttons into More menu? (4+ buttons = show 2 + More)
  get shouldCollapseButtons(): boolean {
    return this.actionButtonCount >= 4;
  }

  // Buttons to show inline (all if <= 3, or top 2 priority if >= 4)
  get inlineButtons(): string[] {
    const allButtons: string[] = [];
    if (this.canConfirmPurchaseOrder) allButtons.push('confirm');
    if (this.canEditPurchaseOrder && !this.isEditing) allButtons.push('edit');
    if (this.canReceiveItems) allButtons.push('receive');
    if (this.canReturnItems) allButtons.push('return');
    if (this.canMarkAsCompleted) allButtons.push('complete');
    if (this.canRevertToDraft) allButtons.push('revert');
    if (this.canCancelPO) allButtons.push('cancel');
    allButtons.push('duplicate'); // Always available

    if (!this.shouldCollapseButtons) {
      return allButtons; // Show all buttons inline
    }

    // When collapsed, show top 2 priority buttons
    const priority: string[] = [];
    // First priority: main action for current status
    if (this.canConfirmPurchaseOrder) priority.push('confirm');
    else if (this.canReceiveItems) priority.push('receive');
    else if (this.canMarkAsCompleted) priority.push('complete');
    // Second priority
    if (this.canReturnItems && priority.length < 2) priority.push('return');
    if (this.canEditPurchaseOrder && !this.isEditing && priority.length < 2) priority.push('edit');
    if (this.canRevertToDraft && priority.length < 2) priority.push('revert');
    if (priority.length < 2) priority.push('duplicate');
    return priority;
  }

  // Buttons to show in More menu (only when collapsed)
  get moreMenuButtons(): string[] {
    if (!this.shouldCollapseButtons) return [];
    const allButtons: string[] = [];
    if (this.canConfirmPurchaseOrder) allButtons.push('confirm');
    if (this.canEditPurchaseOrder && !this.isEditing) allButtons.push('edit');
    if (this.canReceiveItems) allButtons.push('receive');
    if (this.canReturnItems) allButtons.push('return');
    if (this.canMarkAsCompleted) allButtons.push('complete');
    if (this.canRevertToDraft) allButtons.push('revert');
    if (this.canCancelPO) allButtons.push('cancel');
    allButtons.push('duplicate');
    return allButtons.filter(btn => !this.inlineButtons.includes(btn));
  }

  get hasItemsToReturn(): boolean {
    return Object.values(this.returnQuantities).some(qty => qty > 0);
  }

  get isAllItemsReceived(): boolean {
    if (!this.purchaseOrder?.items) return false;
    return this.purchaseOrder.items.every(item =>
      (item.receivedQuantity || 0) >= item.orderedQuantity
    );
  }

  // Filtered activity history (only status changes and email events)
  // Receive and return events are shown in Transaction History section
  get filteredAuditHistory(): IPurchaseOrderHistoryItem[] {
    return this.auditHistory.filter(event =>
      event.type === 'status_change' || event.type === 'email_sent'
    );
  }

  // Computed properties for attachments
  get purchaseOrderId(): number | undefined {
    if (this.purchaseOrder?.id) {
      return parseInt(this.purchaseOrder?.id.toString());
    }
    return undefined;
  }

  get attachments(): any[] {
    return this.purchaseOrder?.getAttachments() || [];
  }

  get canEdit(): boolean {
    return this.purchaseOrder?.canEdit() || false;
  }

  get canEditAttachments(): boolean {
    // Invoice attachments can be added/removed for all PO statuses
    return this.purchaseOrder ? true : false;
  }

  private async handleReceiveItems() {
    if (!this.purchaseOrder) return;

    // Always show review dialog for user confirmation
    this.showReceiveReviewDialog = true;
  }

  private async processReceiveItems() {
    if (!this.purchaseOrder) return;
    
    this.receiving = true;
    
    try {
      const updates = Object.entries(this.receiveQuantities)
        .filter(([_, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({
          id: Number(id),
          receivedQuantity: Number(qty)
        }));

      // Prepare cost data - always send cost data when costs are entered
      let costData: IPurchaseOrderCostData | undefined = undefined;
      const costItems = Object.entries(this.receiveCosts)
        .filter(([id, cost]) => Number(cost) > 0 && updates.some(u => u.id === Number(id)))
        .map(([id, cost]) => {
          const numId = Number(id);
          const quantity = Number(this.receiveQuantities[numId]) || 0;
          const unitCost = Number(cost);
          return {
            id: numId,
            totalCost: quantity * unitCost,
            unitCost: unitCost
          };
        });

      if (costItems.length > 0) {
        costData = {
          enabled: true,
          method: this.shopifyCostUpdateMethod as 'none' | 'latest' | 'average',
          updateShopifyCosts: this.shopifyCostUpdateMethod !== 'none',
          items: costItems
        };
      }
      
      const response = await this.purchaseOrder.receiveItems(
        updates,
        this.receiveNotes,
        this.deliveryNoteNumber,
        this.updateShopifyInventory,
        costData,
        this.markAsCompleteAfterReceive  // Pass mark as complete flag
      );

      const wasMarkedComplete = this.markAsCompleteAfterReceive;

      this.persistCostUpdateMethod();

      this.showReceiveDialog = false;
      this.initializeReceiveData();
      this.receiveNotes = '';
      this.deliveryNoteNumber = '';
      this.markAsCompleteAfterReceive = false;  // Reset the checkbox

      // Check inventory sync status
      const inventoryStatus = response?.inventoryUpdateStatus || 'unknown';
      const overReceivingCount = this.overReceivingItems.length;

      let successMessage = wasMarkedComplete
        ? 'Items received and purchase order marked as complete'
        : overReceivingCount > 0
          ? `Items received successfully (${overReceivingCount} item${overReceivingCount > 1 ? 's' : ''} over-received)`
          : 'Items received successfully';

      // Add status-specific messaging
      if (inventoryStatus === 'failed') {
        successMessage += ' (Note: Shopify inventory sync failed - can be retried)';
      } else if (inventoryStatus === 'partial') {
        successMessage += ' (Note: Some Shopify inventory updates failed)';
      } else if (inventoryStatus === 'already_synced') {
        successMessage += ' (Inventory already synced)';
      }

      const messageColor = inventoryStatus === 'failed' || inventoryStatus === 'partial'
        ? 'warning'
        : 'success';

      EventBus.$emit('show-snackbar', successMessage, messageColor);
      
      // Reload the purchase order data
      await this.fetchData();
      
    } catch (error: any) {
      console.error('Error receiving items:', error);
      EventBus.$emit('show-snackbar', 'There was an error receiving items, please contact our support team.', 'error');
    } finally {
      this.receiving = false;
    }
  }

  // Computed property for dynamic table headers
  get receiveTableHeaders(): any[] {
    const isForeign = this.isForeignCurrencyPO;
    const storeCurr = this.storeCurrency;

    // Responsive headers based on screen size
    if (this.$vuetify.breakpoint.lgAndUp) {
      const headers: any[] = [
        { text: 'Image', value: 'variant_image', sortable: false, align: 'start', width: '80px' },
        { text: 'Product', value: 'productName', width: isForeign ? '28%' : '35%' },
        { text: 'Status', value: 'quantityStatus', align: 'left', width: '150px', sortable: false },
        { text: 'Receive Qty', value: 'receiveQuantity', align: 'right', width: '110px' },
      ];
      if (isForeign) {
        headers.push({ text: `Unit Cost (${storeCurr})`, value: 'unitCostBase', align: 'right', width: '110px', sortable: false });
      }
      headers.push(
        { text: isForeign ? `Landed Cost (${storeCurr})` : 'Unit Cost', value: 'unitCost', align: 'right', width: '110px', sortable: false },
        { text: 'Total Cost', value: 'totalCost', align: 'right', width: '110px', sortable: false }
      );
      return headers;
    } else {
      return [
        { text: 'Product', value: 'productName', width: '40%' },
        { text: 'Status', value: 'quantityStatus', align: 'left', width: '25%', sortable: false },
        { text: 'Qty', value: 'receiveQuantity', align: 'right', width: '15%' },
        { text: isForeign ? 'Landed' : 'Cost', value: 'unitCost', align: 'right', width: '20%', sortable: false }
      ];
    }
  }

  // F1.2: Validation computed properties preserved exactly as-is
  // These handle manual entry validation and remain completely unchanged
  get hasValidQuantities(): boolean {
    if (!this.purchaseOrder?.items) return false;
    
    // Check if any quantities are entered and valid
    const validEntries = Object.entries(this.receiveQuantities).some(([id, qty]) => {
      if (qty !== undefined && qty !== null && qty !== '') {
        const item = this.purchaseOrder!.items.find(i => Number(i.id) === Number(id));
        if (!item) return false;
        const numQty = Number(qty);
        return !isNaN(numQty) && numQty > 0; // Allow over-receiving
      }
      return true; // Skip empty or undefined quantities
    });

    // Return true if we have valid entries and no invalid quantities
    return validEntries && this.invalidQuantityItems.length === 0;
  }

  get invalidQuantityItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];
    
    return this.purchaseOrder.items.filter(item => {
      if (!item.id) return false;
      const qty = this.receiveQuantities[item.id];
      
      // Skip empty or zero quantities
      if (qty === undefined || qty === null || qty === '' || Number(qty) === 0) return false;
      
      const numQty = Number(qty);
      
      // Only check for invalid number or negative quantity (allow over-receiving)
      return isNaN(numQty) || numQty < 0;
    });
  }

  private isOverReceiving(item: IPurchaseOrderItem): boolean {
    if (!item.id) return false;
    const qty = this.receiveQuantities[item.id];
    
    if (qty === undefined || qty === null || qty === '' || Number(qty) === 0) return false;
    
    const numQty = Number(qty);
    const remaining = item.orderedQuantity - (item.receivedQuantity || 0);
    
    // Over-receiving if: receiving more than remaining OR receiving more than total ordered quantity
    return !isNaN(numQty) && numQty > 0 && (numQty > remaining || numQty > item.orderedQuantity);
  }

  get overReceivingItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];

    return this.purchaseOrder.items.filter(item => this.isOverReceiving(item));
  }

  get hasAnyCostsEntered(): boolean {
    return Object.values(this.receiveCosts).some(cost =>
      cost !== undefined && cost !== null && Number(cost) > 0
    );
  }

  get sortedReceiveHistory(): IPurchaseReceived[] {
    if (!this.purchaseOrder?.receiveds) return [];

    // Sort by ID descending (latest first)
    return [...this.purchaseOrder.receiveds].sort((a, b) => {
      const idA = Number(a.id) || 0;
      const idB = Number(b.id) || 0;
      return idB - idA;
    });
  }

  // Combined history of receives and returns
  get combinedTransactionHistory(): any[] {
    // Get return IDs to exclude from receiveds (prevent duplicates)
    const returnIds = new Set(this.returnHistory.map(r => r.id));

    // Filter receiveds to exclude any IDs that appear in returnHistory
    const receives = (this.purchaseOrder?.receiveds || [])
      .filter(r => !returnIds.has(r.id)) // Exclude by ID to prevent duplicates
      .map(r => ({
        ...r,
        type: 'receive', // These are all receives
        date: r.receivedDate
      }));

    const returns = this.returnHistory.map(r => ({
      ...r,
      type: 'return', // These are all returns
      date: r.returnDate,
      receivedDate: r.returnDate // Normalize date field for template
    }));

    // Combine and sort by date descending (latest first)
    return [...receives, ...returns].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });
  }

  get filteredReceiveItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];
    
    const searchQuery = this.receiveSearchQuery.toLowerCase().trim();
    
    if (!searchQuery) {
      return this.purchaseOrder.items;
    }
    
    return this.purchaseOrder.items.filter(item => {
      const productName = (item.productName || '').toLowerCase();
      const variant = (item.variant || '').toLowerCase();
      const sku = (item.sku || '').toLowerCase();
      const supplierSku = (item.supplierSku || '').toLowerCase();
      
      return productName.includes(searchQuery) ||
             variant.includes(searchQuery) ||
             sku.includes(searchQuery) ||
             supplierSku.includes(searchQuery);
    });
  }

  get totalReceivingItems(): number {
    return Object.entries(this.receiveQuantities)
      .filter(([_, qty]) => Number(qty) > 0)
      .length;
  }

  get totalReceivingQuantity(): number {
    return Object.values(this.receiveQuantities)
      .reduce((total: number, qty: number | string) => total + Number(qty || 0), 0);
  }

  get totalReceivingCost(): number {
    return Object.values(this.totalCostsByItem).reduce((sum, cost) => sum + cost, 0);
  }

  get itemsBeingReceived(): any[] {
    if (!this.purchaseOrder?.items) return [];
    
    return Object.entries(this.receiveQuantities)
      .filter(([_, qty]) => Number(qty) > 0)
      .map(([id, qty]) => {
        const item = this.purchaseOrder!.items.find(i => i.id === Number(id));
        if (!item) return null;
        
        const receivingQty = Number(qty);
        const totalReceived = (item.receivedQuantity || 0) + receivingQty;
        
        return {
          id: item.id,
          productName: item.productName,
          receivingQuantity: receivingQty,
          orderedQuantity: item.orderedQuantity,
          receivedQuantity: item.receivedQuantity || 0,
          isOverReceiving: totalReceived > item.orderedQuantity
        };
      })
      .filter(item => item !== null);
  }

  getProductName(purchaseOrderItemId: number): string {
    const item = this.purchaseOrder?.items.find(i => i.id === purchaseOrderItemId);
    return item ? item.productName : '-';
  }

  private calculateTotalQuantity(items: any[] = []): number {
    return items.reduce((total, item) => {
      // Handle both received and returned quantities
      const quantity = Number(item.receivedQuantity || item.returnedQuantity || 0);
      return total + (isNaN(quantity) ? 0 : quantity);
    }, 0);
  }

  private getInventoryStatusColor(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'success';
      case 'already_synced':
        return 'info';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      case 'pending':
        return 'orange';
      case 'skipped':
        return 'grey';
      default:
        return 'grey';
    }
  }

  private getInventoryStatusIcon(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'mdi-check-circle';
      case 'already_synced':
        return 'mdi-information';
      case 'partial':
        return 'mdi-alert';
      case 'failed':
        return 'mdi-alert-circle';
      case 'skipped':
        return 'mdi-minus-circle';
      case 'pending':
        return 'mdi-clock-outline';
      default:
        return 'mdi-help-circle';
    }
  }

  private getInventoryStatusText(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'Updated';
      case 'already_synced':
        return 'Already Synced';
      case 'partial':
        return 'Partially Updated';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      case 'pending':
        return 'Pending';
      default:
        return 'Legacy';  // For old records without status
    }
  }

  private showReceiveDetail(receive: IPurchaseReceived) {
    this.selectedReceive = receive;
    this.showReceiveDetailDialog = true;
  }

  private getReceiveCostUpdateStatusColor(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'grey';

    const statusColorMap: Record<string, string> = {
      'synced': 'success',
      'updated': 'success',
      'skipped': 'grey',
      'failed': 'error',
      'pending': 'orange',
      'partial': 'warning',
      'not_applicable': 'grey'
    };

    return statusColorMap[status] || 'grey';
  }

  private getReceiveCostUpdateStatusIcon(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'mdi-minus-circle';

    const statusIconMap: Record<string, string> = {
      'synced': 'mdi-check-circle',
      'updated': 'mdi-check-circle',
      'skipped': 'mdi-skip-next-circle',
      'failed': 'mdi-alert-circle',
      'pending': 'mdi-clock-outline',
      'partial': 'mdi-alert',
      'not_applicable': 'mdi-minus-circle'
    };

    return statusIconMap[status] || 'mdi-minus-circle';
  }

  private getReceiveCostUpdateStatusText(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'Not Updated';

    // Get the cost calculation method
    const method = this.getCostCalculationMethod(receive);

    const statusTextMap: Record<string, string> = {
      'synced': method ? `Updated (${method})` : 'Updated',
      'updated': method ? `Updated (${method})` : 'Updated',
      'skipped': method ? `Skipped (${method})` : 'Skipped',
      'failed': method ? `Failed (${method})` : 'Failed',
      'pending': method ? `Pending (${method})` : 'Pending',
      'partial': method ? `Partially Updated (${method})` : 'Partially Updated',
      'not_applicable': 'Not Applicable'
    };

    return statusTextMap[status] || 'Unknown';
  }

  private getCostCalculationMethod(receive: IPurchaseReceived | null): string | null {
    if (!receive) return null;
    
    // Check if the receive has unitCostCalculationMethod from model
    if ((receive as any).unitCostCalculationMethod) {
      const method = (receive as any).unitCostCalculationMethod;
      return method === 'latest' ? 'Latest Cost' : method === 'average' ? 'Weighted Avg' : method;
    }
    
    return null;
  }

  private deriveShopifyCostStatus(receive: IPurchaseReceived | null): string | null {
    if (!receive) return null;

    const receiveAny = receive as any;

    // First, check if backend provided the status from JSONB metadata
    if (receiveAny.shopifyCostSyncMetadata?.status) {
      return receiveAny.shopifyCostSyncMetadata.status;
    }

    // Fallback: check if the receive has shopifyCostStatus already derived from backend
    if (receiveAny.shopifyCostStatus) {
      return receiveAny.shopifyCostStatus;
    }

    // Check if cost update was enabled
    if (receiveAny.isUpdateUnitCostShopify === false || receiveAny.isUpdateUnitCostShopify === null) {
      return 'not_applicable'; // No cost update was attempted
    }

    // Check item-level metadata to determine overall status
    if (receive.items && receive.items.length > 0) {
      const itemStatuses = receive.items.map((item: any) =>
        item.shopifyCostSyncMetadata?.status || 'pending'
      );

      // Count status types
      const syncedCount = itemStatuses.filter(s => s === 'synced').length;
      const failedCount = itemStatuses.filter(s => s === 'failed').length;
      const pendingCount = itemStatuses.filter(s => s === 'pending').length;
      const notApplicableCount = itemStatuses.filter(s => s === 'not_applicable').length;

      // Determine overall status based on item statuses
      if (failedCount > 0 && syncedCount === 0) {
        return 'failed'; // All attempts failed
      } else if (failedCount > 0 && syncedCount > 0) {
        return 'partial'; // Mixed success and failure
      } else if (syncedCount > 0) {
        return 'synced'; // At least some succeeded
      } else if (pendingCount > 0) {
        return 'pending'; // Still pending
      } else if (notApplicableCount === itemStatuses.length) {
        return 'not_applicable'; // All not applicable
      }
    }

    // Fallback to pending if cost updates enabled
    if (receiveAny.isUpdateUnitCostShopify === true) {
      return 'pending';
    }

    return 'not_applicable';
  }

  private getReceiveItemsCount(receive: IPurchaseReceived | null): number {
    return receive?.items?.length || 0;
  }

  private getReceiveTotalQuantity(receive: IPurchaseReceived | null): number {
    return receive?.items?.reduce((total, item) => total + (parseInt(item.receivedQuantity.toString()) || 0), 0) || 0;
  }

  private getReceiveTotalAmount(receive: IPurchaseReceived | null): number {
    if (!receive || !receive.items || !this.purchaseOrder) return 0;

    return receive.items.reduce((total, receiveItem) => {
      return total + (receiveItem.totalCost || 0);
    }, 0);
  }

  /**
   * Get color for item-level sync status
   */
  private getItemSyncStatusColor(status: string | undefined): string {
    if (!status) return 'grey';

    switch (status) {
      case 'synced':
        return 'success';
      case 'pending':
        return 'orange';
      case 'failed':
        return 'error';
      case 'skipped':
      case 'not_applicable':
        return 'grey';
      default:
        return 'grey';
    }
  }

  /**
   * Get icon for item-level sync status
   */
  private getItemSyncStatusIcon(status: string | undefined): string {
    if (!status) return 'mdi-help-circle';

    switch (status) {
      case 'synced':
        return 'mdi-check-circle';
      case 'pending':
        return 'mdi-clock-outline';
      case 'failed':
        return 'mdi-alert-circle';
      case 'skipped':
      case 'not_applicable':
        return 'mdi-minus-circle';
      default:
        return 'mdi-help-circle';
    }
  }

  /**
   * Parse and format error message from JSON string or object
   */
  private getItemErrorMessage(error: string | object | null): string {
    if (!error) return '';

    try {
      if (typeof error === 'string') {
        const parsed = JSON.parse(error);
        return parsed.error || parsed.message || error;
      } else if (typeof error === 'object') {
        return (error as any).error || (error as any).message || JSON.stringify(error);
      }
      return String(error);
    } catch (e) {
      return String(error);
    }
  }

  private getShopifyVariantDetailUrl(productId: number, variantId: number): string {
    if (!this.store || !this.store.shopName || !productId || !variantId) return '#';
    
    const shopName = this.store.shopName.replace('.myshopify.com', '');
    return `https://admin.shopify.com/store/${shopName}/products/${productId}/variants/${variantId}`;
  }

  private get receiveDetailHeaders() {
    const isForeign = this.isForeignCurrencyPO;
    const storeCurr = this.storeCurrency;
    const headers: any[] = [
      { text: 'Product', value: 'product', sortable: false },
      { text: 'Received Qty', value: 'receivedQuantity', align: 'right', sortable: false },
      { text: isForeign ? `Unit Cost (${storeCurr})` : 'Unit Cost', value: 'unitCost', align: 'right', sortable: false },
    ];
    if (isForeign) {
      headers.push({ text: `Landed Cost (${storeCurr})`, value: 'landedCost', align: 'right', sortable: false });
    }
    headers.push(
      { text: 'Total Cost', value: 'totalCost', align: 'right', sortable: false },
      { text: 'Inventory Sync', value: 'inventorySync', align: 'center', sortable: false },
      { text: 'Cost Sync', value: 'costSync', align: 'center', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false, width: '150px' }
    );
    return headers;
  }

  private getReceiveDetailItems(receive: IPurchaseReceived | null) {
    if (!receive || !receive.items || !this.purchaseOrder) return [];

    return receive.items.map(receiveItem => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === receiveItem.purchaseOrderItemId);
      if (!orderItem) return null;

      return {
        id: receiveItem.id,
        purchaseOrderItemId: receiveItem.purchaseOrderItemId,
        variantId: orderItem.variantId,
        inventoryItemId: receiveItem.inventoryItemId,
        product: orderItem.productName,
        variant: orderItem.variant || 'Default',
        sku: orderItem.sku || 'N/A',
        supplierSku: orderItem.supplierSku || orderItem.sku || 'N/A',
        receivedQuantity: receiveItem.receivedQuantity,
        unitCost: receiveItem.unitCost,
        landedCost: orderItem.extra?.landed_cost ?? null,
        totalCost: receiveItem.totalCost,
        variantImage: orderItem.variantImage,
        // Add sync metadata
        inventorySyncStatus: (receiveItem as any).shopifyInventorySyncMetadata?.status,
        inventorySyncError: (receiveItem as any).shopifyInventorySyncMetadata?.error,
        costSyncStatus: (receiveItem as any).shopifyCostSyncMetadata?.status,
        costSyncError: (receiveItem as any).shopifyCostSyncMetadata?.error
      };
    }).filter(item => item !== null);
  }

  private getShopifyInventoryHistoryUrl(inventoryItemId: number, locationId: number): string {
    if (!this.store || !this.store.shopName) return '#';
    
    const shopName = this.store.shopName.replace('.myshopify.com', '');
    return `https://admin.shopify.com/store/${shopName}/products/inventory/${inventoryItemId}/inventory_history?location_id=${locationId}`;
  }

  private async handleMarkAsCompleted() {
    if (!this.purchaseOrder) return;

    this.markingAsCompleted = true;
    try {
      await this.purchaseOrder.updateStatus('completed');

      this.showMarkAsCompletedDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order marked as completed successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to mark purchase order as completed: ${error.message}`, 'error');
      console.error('Error marking purchase order as completed:', error);
    } finally {
      this.markingAsCompleted = false;
    }
  }

  private async handleCancelPO() {
    if (!this.purchaseOrder) return;

    this.canceling = true;
    try {
      await this.purchaseOrder.updateStatus('canceled');

      this.showCancelDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order canceled successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to cancel purchase order: ${error.message}`, 'error');
      console.error('Error canceling purchase order:', error);
    } finally {
      this.canceling = false;
    }
  }

  private async handleRevertToDraft() {
    if (!this.purchaseOrder) return;

    this.revertingToDraft = true;
    try {
      await this.purchaseOrder.updateStatus('draft');

      this.showRevertToDraftDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order reverted to draft successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to revert purchase order: ${error.message}`, 'error');
      console.error('Error reverting purchase order to draft:', error);
    } finally {
      this.revertingToDraft = false;
    }
  }

  // Add computed property
  private get canConfirmPurchaseOrder(): boolean {
    return this.purchaseOrder?.status === 'draft';
  }

  // Add confirm method
  private async confirmPurchaseOrder() {
    if (!this.purchaseOrder) return;

    this.confirming = true;
    try {
      await this.purchaseOrder.updateStatus('confirmed');
      EventBus.$emit('show-snackbar', 'Purchase order confirmed successfully', 'success');
      await this.fetchData(); // Refresh data to get updated status
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to confirm purchase order: ${error.message}`, 'error');
      console.error('Error confirming purchase order:', error);
    } finally {
      this.confirming = false;
    }
  }

  private async confirmDuplicatePurchaseOrder() {
    if (this.duplicating) return;
    if (this.isEditing) return;
    if (!this.purchaseOrder?.id) return;

    this.duplicating = true;
    try {
      const newPO = await PurchaseOrderModel.duplicate(this.purchaseOrder.id);
      this.showDuplicateDialog = false;
      EventBus.$emit('show-snackbar', `Purchase order duplicated as ${newPO.poNumber}`, 'success');
      await this.$router.push(`/purchase-orders/${newPO.id}`);
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to duplicate purchase order: ${error.message}`, 'error');
      console.error('Error duplicating purchase order:', error);
    } finally {
      this.duplicating = false;
    }
  }

  private confirmAndReceive() {
    this.showReceiveReviewDialog = false;
    
    // Process either manual receive or CSV import based on current mode
    if (this.receiveMode === 'import' && this.importFile) {
      this.processImportReceive();
    } else {
      this.processReceiveItems();
    }
  }

  private cancelReceiveReview() {
    this.showReceiveReviewDialog = false;
  }

  // Cost tracking methods
  // Computed property to cache total costs for all items
  get totalCostsByItem(): { [key: number]: number } {
    const costs: { [key: number]: number } = {};
    
    Object.keys(this.receiveQuantities).forEach(itemIdStr => {
      const itemId = Number(itemIdStr);
      const quantity = Number(this.receiveQuantities[itemId]) || 0;
      const unitCost = Number(this.receiveCosts[itemId]) || 0;
      costs[itemId] = quantity * unitCost;
    });
    
    return costs;
  }

  private getTotalCost(itemId: number): number {
    return this.totalCostsByItem[itemId] || 0;
  }

  // Quick action methods
  private fillAllOutstanding(): void {
    if (!this.purchaseOrder) return;
    
    // Build new quantities object with all items pre-initialized
    const newQuantities: { [key: number]: number | string } = {};
    
    // First, initialize all items to ensure reactivity
    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        // Keep existing value if not in filtered view
        newQuantities[item.id] = this.receiveQuantities[item.id] || '';
      }
    });
    
    // Then fill only the filtered items with outstanding quantities
    this.filteredReceiveItems.forEach(item => {
      if (item.id) {
        const outstanding = item.orderedQuantity - (item.receivedQuantity || 0);
        // Set value for items with outstanding quantities, empty string for others
        newQuantities[item.id] = outstanding > 0 ? outstanding : '';
      }
    });
    
    // Replace the entire object to trigger Vue reactivity
    this.receiveQuantities = { ...newQuantities };
  }

  private clearAllQuantities(): void {
    if (!this.purchaseOrder) return;
    
    // Build new quantities object preserving non-filtered items
    const newQuantities: { [key: number]: number | string } = {};
    const newCosts: { [key: number]: number } = {};
    
    // Initialize all items preserving values for non-filtered items
    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        // Keep existing value if not in filtered view
        const isFiltered = this.filteredReceiveItems.some(f => f.id === item.id);
        newQuantities[item.id] = isFiltered ? '' : (this.receiveQuantities[item.id] || '');
        newCosts[item.id] = isFiltered ? 0 : (this.receiveCosts[item.id] || 0);
      }
    });
    
    // Replace objects to trigger Vue reactivity
    this.receiveQuantities = { ...newQuantities };
    this.receiveCosts = { ...newCosts };
  }

  private initializeReceiveData(): void {
    if (!this.purchaseOrder) return;

    // Pre-initialize both objects with all item IDs to prevent reactive addition during render
    const quantities: { [key: number]: number | string } = {};
    const costs: { [key: number]: number } = {};

    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        quantities[item.id] = '';
        // For foreign POs: pre-fill with landed cost (store currency) for Shopify
        // For same-currency POs: pre-fill with unit price (current behavior)
        if (this.isForeignCurrencyPO && item.extra?.landed_cost != null) {
          costs[item.id] = item.extra.landed_cost;
        } else {
          costs[item.id] = item.unitPrice || 0;
        }
      }
    });

    this.receiveQuantities = quantities;
    this.receiveCosts = costs;
  }

  private scrollToSettings() {
    // Scroll to the settings section for small screens
    const settingsSection = document.getElementById('settings-section');
    if (settingsSection) {
      settingsSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  private handleScroll() {
    // Show settings indicator only on small screens when settings are not visible
    if (!this.$vuetify.breakpoint.lgAndUp) {
      const scrollContainer = this.$refs.scrollContainer as HTMLElement;
      const settingsSection = document.getElementById('settings-section');
      
      if (scrollContainer && settingsSection) {
        const containerBottom = scrollContainer.scrollTop + scrollContainer.clientHeight;
        const settingsTop = settingsSection.offsetTop;
        
        // Show indicator if settings section is not visible
        this.showSettingsIndicator = containerBottom < settingsTop + 100;
      }
    } else {
      this.showSettingsIndicator = false;
    }
  }

  private persistCostUpdateMethod(): void {
    try {
      localStorage.setItem('po_receive_cost_method', this.shopifyCostUpdateMethod);
    } catch {
      // Ignore storage errors (quota exceeded, private browsing)
    }
  }

  private getShopifyCostUpdateHint(): string {
    switch (this.shopifyCostUpdateMethod) {
      case 'latest':
        return 'Product unit costs will be updated in Shopify using the received costs';
      case 'average':
        return 'Product unit costs will be calculated using weighted average with current Shopify inventory';
      case 'none':
      default:
        return 'Product unit costs in Shopify will remain unchanged';
    }
  }

  private showSuccessMessage(message: string): void {
    EventBus.$emit('show-snackbar', message, 'success');
  }

  private showErrorMessage(message: string): void {
    EventBus.$emit('show-snackbar', message, 'error');
  }


  private openInteractiveGuide(): void {
    window.open('https://app.arcade.software/share/QQIgbzT3DY2Fu3QF1xIU', '_blank');
  }

  // Add placeholder methods for import functionality
  private async downloadReceiveTemplate() {
    if (!this.purchaseOrder) return;
    
    this.downloadingTemplate = true;
    try {
      // Use the model method to download CSV template
      const blob = await this.purchaseOrder.downloadReceiveTemplate();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      const filename = `PO-${this.purchaseOrder.poNumber}-receive-template.csv`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      EventBus.$emit('show-snackbar', 'CSV template downloaded successfully', 'success');
      
    } catch (error: any) {
      console.error('Error downloading template:', error);
      EventBus.$emit('show-snackbar', `Failed to download template: ${error.message}`, 'error');
    } finally {
      this.downloadingTemplate = false;
    }
  }

  private async handleImportReceive() {
    if (!this.purchaseOrder || !this.importFile) return;
    
    // Show review dialog for user confirmation (like manual receive)
    this.showReceiveReviewDialog = true;
  }

  private async processImportReceive() {
    if (!this.purchaseOrder || !this.importFile) return;
    
    this.importingReceive = true;
    
    try {
      // Prepare cost data - always send cost data when costs are entered
      let costData: IPurchaseOrderCostData | undefined = undefined;
      const costItems = Object.entries(this.receiveCosts)
        .filter(([id, cost]) => Number(cost) > 0)
        .map(([id, cost]) => {
          const numId = Number(id);
          const quantity = Number(this.receiveQuantities[numId]) || 1; // Default to 1 for CSV import
          const unitCost = Number(cost);
          return {
            id: numId,
            totalCost: quantity * unitCost,
            unitCost: unitCost
          };
        });

      // For CSV import, always send cost settings even if no manual cost items
      // The CSV file itself may contain unit costs
      if (costItems.length > 0 || this.receiveMode === 'import') {
        costData = {
          enabled: this.shopifyCostUpdateMethod !== 'none',
          method: this.shopifyCostUpdateMethod as 'none' | 'latest' | 'average',
          updateShopifyCosts: this.shopifyCostUpdateMethod !== 'none',
          items: costItems
        };
      }

      // Call the model method to import CSV
      const response = await this.purchaseOrder.importReceiveFromCSV(
        this.importFile,
        this.deliveryNoteNumber,
        this.receiveNotes,
        this.updateShopifyInventory,
        costData
      );
      
      this.persistCostUpdateMethod();

      // Close dialog and reset state
      this.showReceiveDialog = false;
      this.initializeReceiveData();
      this.receiveNotes = '';
      this.deliveryNoteNumber = '';
      this.importFile = null;
      this.receiveMode = 'manual';
      
      // Show detailed import summary
      this.showImportSummary(response);
      
      // Refresh the purchase order data
      await this.fetchData();
      
    } catch (error: any) {
      // Extract error message from different possible error structures
      let errorMessage = 'Failed to import CSV';
      
      // Debug logging
      console.error('Import CSV Error:', error);
      if (error.response) {
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response.data);
        console.error('Error response data type:', typeof error.response.data);
      }
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        // The response data might be already parsed by axios or might be a string
        const responseData = error.response.data;
        
        if (typeof responseData === 'object') {
          // Already parsed object
          if (responseData.message) {
            errorMessage = responseData.message;
          } else if (responseData.error) {
            errorMessage = responseData.error;
          } else {
            // Try to find any error-like property
            const errorKeys = ['msg', 'err', 'error_message', 'errorMessage'];
            for (const key of errorKeys) {
              if (responseData[key]) {
                errorMessage = responseData[key];
                break;
              }
            }
          }
        } else if (typeof responseData === 'string') {
          // Response is a string, might need parsing
          try {
            const parsedData = JSON.parse(responseData);
            if (parsedData.message) {
              errorMessage = parsedData.message;
            } else if (typeof parsedData === 'string') {
              errorMessage = parsedData;
            }
          } catch (e) {
            // If parsing fails, use the string as is
            errorMessage = responseData;
          }
        }
      } else if (error.body && error.body.message) {
        // Fetch-style error response
        errorMessage = error.body.message;
      } else if (error.data && error.data.message) {
        // Direct data property
        errorMessage = error.data.message;
      } else if (error.message) {
        // Standard error message
        errorMessage = error.message;
      }
      
      EventBus.$emit('show-snackbar', {
        message: errorMessage,
        color: 'error',
      });
    } finally {
      this.importingReceive = false;
      this.showReceiveDialog = false;
    }
  }

  private showImportSummary(response: any) {
    const {
      totalRowsInCsv = 0,
      successfullyProcessedRows = 0,
      failedRows = 0,
      errorDetails = [],
      receiveRecordId = null,
      inventoryUpdateStatus = 'skipped',
      // NEW: Cost tracking fields
      costUpdateStatus = 'skipped',
      costCalculationMethod = null,
      itemsWithCosts = 0
    } = response;

    // Store the summary data
    this.importSummaryData = {
      totalRowsInCsv,
      successfullyProcessedRows,
      failedRows,
      errorDetails,
      receiveRecordId,
      inventoryUpdateStatus,
      // NEW: Cost tracking data
      costUpdateStatus,
      costCalculationMethod,
      itemsWithCosts
    };

    // Show the summary dialog
    this.showImportSummaryDialog = true;

    // Also show a brief snackbar for quick feedback
    if (failedRows > 0) {
      EventBus.$emit('show-snackbar', 
        `Import completed: ${successfullyProcessedRows} successful, ${failedRows} failed`, 
        'warning'
      );
    } else {
      EventBus.$emit('show-snackbar', 
        `All ${successfullyProcessedRows} items imported successfully`, 
        'success'
      );
    }
  }

  /**
   * Retry Shopify inventory sync for failed receive record
   */
  private async retryShopifySync() {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Purchase order not loaded',
        'error'
      );
      return;
    }

    if (!this.importSummaryData.receiveRecordId) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Receive record ID not found',
        'error'
      );
      return;
    }

    this.retryingSyncStatus = true;

    try {
      const response = await this.purchaseOrder.retryShopifyInventorySync(
        this.importSummaryData.receiveRecordId
      );

      // Update the import summary data with new status
      this.importSummaryData.inventoryUpdateStatus = response.inventoryUpdateStatus || 'updated';

      EventBus.$emit('show-snackbar',
        'Shopify inventory sync completed successfully',
        'success'
      );

      // Refresh the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingSyncStatus = false;
    }
  }

  /**
   * Retry cost sync for a failed receive record
   */
  private async retryCostSync() {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Purchase order not loaded',
        'error'
      );
      return;
    }

    if (!this.importSummaryData.receiveRecordId) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Receive record ID not found',
        'error'
      );
      return;
    }

    this.retryingCostSyncStatus = true;

    try {
      const response = await this.purchaseOrder.retryCostSync(
        this.importSummaryData.receiveRecordId
      );

      // Update the import summary data with new status
      this.importSummaryData.costUpdateStatus = response.costUpdateStatus || 'updated';

      EventBus.$emit('show-snackbar',
        'Shopify cost sync completed successfully',
        'success'
      );

      // Refresh the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry cost sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingCostSyncStatus = false;
    }
  }

  /**
   * Retry inventory sync for a selected receive record from history
   */
  private async retryReceiveInventorySync() {
    if (!this.purchaseOrder || !this.selectedReceive) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Receive record not selected',
        'error'
      );
      return;
    }

    const receiveId = this.selectedReceive.id;
    this.retryingInventorySync = true;

    try {
      await this.purchaseOrder.retryShopifyInventorySync(receiveId);

      EventBus.$emit('show-snackbar',
        'Shopify inventory sync retry initiated successfully',
        'success'
      );

      // Refresh the purchase order data to get updated status
      await this.fetchData();

      // Update selectedReceive reference to point to the refreshed data
      this.selectedReceive = this.purchaseOrder?.receiveds?.find(r => r.id === receiveId) || null;
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry inventory sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingInventorySync = false;
    }
  }

  /**
   * Retry cost sync for a selected receive record from history
   */
  private async retryReceiveCostSync() {
    if (!this.purchaseOrder || !this.selectedReceive) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Receive record not selected',
        'error'
      );
      return;
    }

    const receiveId = this.selectedReceive.id;
    this.retryingCostSync = true;

    try {
      await this.purchaseOrder.retryCostSync(receiveId);

      EventBus.$emit('show-snackbar',
        'Shopify cost sync retry initiated successfully',
        'success'
      );

      // Refresh the purchase order data to get updated status
      await this.fetchData();

      // Update selectedReceive reference to point to the refreshed data
      this.selectedReceive = this.purchaseOrder?.receiveds?.find(r => r.id === receiveId) || null;
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry cost sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingCostSync = false;
    }
  }

  /**
   * Helper to determine if inventory retry button should be shown in Receipt Details modal
   */
  private shouldShowInventoryRetryButton(receive: IPurchaseReceived | null): boolean {
    if (!receive) return false;
    return isRetryableStatus(receive.shopifyInventorySyncStatus);
  }

  /**
   * Helper to determine if cost retry button should be shown in Receipt Details modal
   */
  private shouldShowCostRetryButton(receive: IPurchaseReceived | null): boolean {
    if (!receive) return false;

    // Check if cost updates were enabled for this receive
    if (!(receive as any).isUpdateUnitCostShopify) {
      return false;
    }

    const status = this.deriveShopifyCostStatus(receive);
    // Show retry for failed, pending, or partial statuses
    return status === 'failed' || status === 'pending' || status === 'partial';
  }

  /**
   * Retry inventory sync from history table
   */
  private async retryHistoryInventorySync(receive: IPurchaseReceived) {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Purchase order not loaded',
        'error'
      );
      return;
    }

    this.retryingHistoryInventory = receive.id;

    try {
      await this.purchaseOrder.retryShopifyInventorySync(receive.id);

      EventBus.$emit('show-snackbar',
        'Shopify inventory sync retry initiated successfully',
        'success'
      );

      // Refresh the purchase order data to get updated status
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry inventory sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingHistoryInventory = null;
    }
  }

  /**
   * Retry cost sync from history table
   */
  private async retryHistoryCostSync(receive: IPurchaseReceived) {
    if (!this.purchaseOrder) {
      EventBus.$emit('show-snackbar',
        'Cannot retry: Purchase order not loaded',
        'error'
      );
      return;
    }

    this.retryingHistoryCost = receive.id;

    try {
      await this.purchaseOrder.retryCostSync(receive.id);

      EventBus.$emit('show-snackbar',
        'Shopify cost sync retry initiated successfully',
        'success'
      );

      // Refresh the purchase order data to get updated status
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar',
        `Failed to retry cost sync: ${error.message}`,
        'error'
      );
    } finally {
      this.retryingHistoryCost = null;
    }
  }

  /**
   * Helper to determine if inventory retry button should be shown in history table
   */
  private shouldShowHistoryInventoryRetryButton(receive: IPurchaseReceived): boolean {
    return isRetryableStatus(receive.shopifyInventorySyncStatus);
  }

  /**
   * Helper to determine if cost retry button should be shown in history table
   */
  private shouldShowHistoryCostRetryButton(receive: IPurchaseReceived): boolean {
    // Check if cost updates were enabled for this receive
    if (!(receive as any).isUpdateUnitCostShopify) {
      return false;
    }

    const status = this.deriveShopifyCostStatus(receive);
    // Show retry for failed, pending, or partial statuses
    return status === 'failed' || status === 'pending' || status === 'partial';
  }

  /**
   * Helper to determine if inventory retry button should be shown for returns
   */
  private shouldShowReturnInventoryRetryButton(returnRecord: any): boolean {
    const status = returnRecord.shopifyInventorySyncMetadata?.status || returnRecord.shopifyInventorySyncStatus;
    return isRetryableStatus(status);
  }

  // Return Detail Methods
  private viewReturnDetail(returnRecord: any): void {
    this.selectedReturn = returnRecord;
    this.showReturnDetailDialog = true;
  }

  private confirmDeleteReturn(returnRecord: any): void {
    this.returnToDelete = returnRecord;
    this.showDeleteReturnDialog = true;
  }

  // F1.3: Computed properties for import mode UI enhancements
  get csvFileRules() {
    return [
      (v: File | null) => !!v || 'CSV file is required',
      (v: File | null) => !v || v.name.toLowerCase().endsWith('.csv') || 'File must be a CSV file',
      (v: File | null) => !v || v.size <= 5000000 || 'File size must be less than 5MB'
    ];
  }

  get fullyReceivedItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    return this.purchaseOrder.items.filter(item => 
      (item.receivedQuantity || 0) >= item.orderedQuantity
    ).length;
  }

  get partiallyReceivedItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    return this.purchaseOrder.items.filter(item => {
      const received = item.receivedQuantity || 0;
      return received > 0 && received < item.orderedQuantity;
    }).length;
  }

  get pendingItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    return this.purchaseOrder.items.filter(item => 
      (item.receivedQuantity || 0) === 0
    ).length;
  }

  // Add new methods for file handling
  handleFileSelection(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.fileError = 'Please select a valid CSV file';
        this.importFile = null;
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'File size must be less than 5MB';
        this.importFile = null;
        return;
      }
      
      this.importFile = file;
      this.fileError = '';
    }
  }

  clearFileSelection() {
    this.importFile = null;
    this.fileError = '';
    // Reset the file input
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  formatFileSize(size: number): string {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  }

  openFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement).click();
  }

  private checkImportFlag() {
    if (this.$route.query.import === 'true') {
      // Use nextTick to ensure the component has rendered before opening the dialog
      this.$nextTick(() => {
        this.showImportItemsDialog = true;
        // Clean up the URL
        const newQuery = { ...this.$route.query };
        delete newQuery.import;
        this.$router.replace({ query: newQuery }).catch(()=>{});
      });
    }
  }

  private onImportSuccess() {
    EventBus.$emit('show-snackbar', 'Items imported successfully!', 'success');
    this.fetchData(); // Refresh data to show new items
  }

  // Load saved email preferences from localStorage
  private loadEmailPreferences(): void {
    try {
      const saved = localStorage.getItem(this.localStorageKey);
      if (saved !== null) {
        const prefs = JSON.parse(saved);
        // Handle legacy formats
        if (typeof prefs === 'boolean') {
          this.includePricing = prefs;
        } else {
          this.includePricing = prefs.includePricing ?? prefs.includeUnitPrice ?? true;
        }
      }
    } catch (error) {
      console.warn('Failed to load email preferences:', error);
      this.includePricing = true;
    }
  }

  // Save email preferences to localStorage
  private saveEmailPreferences(): void {
    try {
      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify({
          includePricing: this.includePricing
        })
      );
    } catch (error) {
      console.warn('Failed to save email preferences:', error);
    }
  }

  private loadSavedEmails(): void {
    try {
      const saved = localStorage.getItem(this.savedEmailsKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.savedEmails = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('Failed to load saved emails:', error);
      this.savedEmails = [];
    }
    try {
      const savedNames = localStorage.getItem(this.savedCompanyNamesKey);
      if (savedNames) {
        const parsed = JSON.parse(savedNames);
        this.savedCompanyNames = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('Failed to load saved company names:', error);
      this.savedCompanyNames = [];
    }
  }

  private saveSavedEmails(): void {
    try {
      const allEmails = [
        ...this.emailRecipients,
        ...this.replyToEmails,
      ];
      const unique = [...new Set([...this.savedEmails, ...allEmails])].filter(Boolean);
      this.savedEmails = unique.slice(-20);
      localStorage.setItem(this.savedEmailsKey, JSON.stringify(this.savedEmails));

      if (this.companyName) {
        const uniqueNames = [...new Set([...this.savedCompanyNames, this.companyName])].filter(Boolean);
        this.savedCompanyNames = uniqueNames.slice(-10);
        localStorage.setItem(this.savedCompanyNamesKey, JSON.stringify(this.savedCompanyNames));
      }
    } catch (error) {
      console.warn('Failed to save emails:', error);
    }
  }

  // Load export preferences from localStorage
  private loadExportPreferences(): void {
    try {
      const saved = localStorage.getItem('po_export_preferences');
      if (saved) {
        const prefs = JSON.parse(saved);
        // Handle legacy format with separate flags
        this.exportIncludePricing = prefs.includePricing ?? prefs.includeUnitPrice ?? true;
      }
    } catch (error) {
      console.warn('Failed to load export preferences:', error);
    }
  }

  // Save export preferences to localStorage
  private saveExportPreferences(): void {
    try {
      localStorage.setItem('po_export_preferences', JSON.stringify({
        includePricing: this.exportIncludePricing
      }));
    } catch (error) {
      console.warn('Failed to save export preferences:', error);
    }
  }

  private get canImportItems(): boolean {
    if (!this.purchaseOrder) return false;
    return this.purchaseOrder.status === 'draft';
  }

  private get canAddProducts(): boolean {
    if (!this.purchaseOrder) return false;
    const hasSupplierId = !!(this.supplier?.id || this.purchaseOrder.supplierId);
    return this.canEditPurchaseOrder && hasSupplierId;
  }

  // Attachment event handlers
  private async handleAttachmentsAdded(newAttachments: any[]): Promise<void> {
    if (!this.purchaseOrder) return;

    try {
      this.purchaseOrder.addAttachments(newAttachments);
      await this.purchaseOrder.update({});
      // Local state is already updated, no need to reload page
    } catch (error) {
      console.error('Failed to save attachments:', error);
      this.showMessage('Failed to save attachments', 'error');
    }
  }

  private async handleAttachmentRemoved(index: number): Promise<void> {
    if (!this.purchaseOrder) return;

    try {
      this.purchaseOrder.removeAttachment(index);
      await this.purchaseOrder.update({});
      // Local state is already updated, no need to reload page
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      this.showMessage('Failed to remove attachment', 'error');
    }
  }

  // Return Items methods (Story 5)
  private getAvailableToReturn(item: IPurchaseOrderItem): number {
    const received = item.receivedQuantity || 0;
    const returned = item.returnedQuantity || 0;
    return Math.max(0, received - returned);
  }

  private validateReturnQuantity(item: IPurchaseOrderItem): void {
    if (!item.id) return;
    const qty = this.returnQuantities[item.id];
    const available = this.getAvailableToReturn(item);

    if (qty && qty > available) {
      Vue.set(this.returnQuantityErrors, item.id, `Maximum return quantity is ${available}`);
    } else if (qty && qty < 0) {
      Vue.set(this.returnQuantityErrors, item.id, 'Quantity must be positive');
    } else {
      Vue.delete(this.returnQuantityErrors, item.id);
    }
  }

  private getReturnQuantityError(item: IPurchaseOrderItem): string {
    return item.id ? (this.returnQuantityErrors[item.id] || '') : '';
  }

  private closeReturnDialog(): void {
    this.showReturnDialog = false;
    this.showReturnReviewDialog = false;
    this.returnQuantities = {};
    this.returnQuantityErrors = {};
    this.returnReason = '';
    this.returnNotes = '';
    this.returnSearchQuery = '';
  }

  private confirmAndReturn(): void {
    // Don't close the dialog here - let submitReturn() close it after API call
    this.submitReturn();
  }

  private cancelReturnReview(): void {
    this.showReturnReviewDialog = false;
  }

  private async submitReturn(): Promise<void> {
    try {
      this.submittingReturn = true;

      const items = this.returnableItems
        .filter(item => this.returnQuantities[item.id!] && this.returnQuantities[item.id!] > 0)
        .map(item => ({
          purchaseOrderItemId: item.id,
          returnedQuantity: this.returnQuantities[item.id!]
        }));

      const response: any = await post(`/purchase_orders/${this.purchaseOrder!.id}/return`, {
        items,
        reason: this.returnReason || null,
        notes: this.returnNotes || null
      });

      const { inventoryUpdateStatus } = response;
      let message = 'Return created successfully';
      if (inventoryUpdateStatus === 'synced') {
        message = 'Return created and inventory updated successfully';
      } else if (inventoryUpdateStatus === 'partial') {
        message = 'Return created (some inventory items failed to sync)';
      }

      EventBus.$emit('show-snackbar', message, inventoryUpdateStatus === 'synced' ? 'success' : 'warning');
      this.closeReturnDialog();
      await this.fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to submit return';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
      // Keep dialog open on error so user can retry
    } finally {
      this.submittingReturn = false;
    }
  }

  // Return History methods (Story 6)
  private async loadReturnHistory(): Promise<void> {
    if (!this.purchaseOrder?.id) return;
    this.loadingReturnHistory = true;
    try {
      const response: any = await get(`/purchase_orders/${this.purchaseOrder.id}/returns`);
      this.returnHistory = response.data || [];
    } catch (error: any) {
      console.error('Error loading return history:', error);
      EventBus.$emit('show-snackbar', 'Failed to load return history', 'error');
    } finally {
      this.loadingReturnHistory = false;
    }
  }

  private showReturnDetail(returnRecord: any): void {
    this.selectedReturn = returnRecord;
    this.showReturnDetailDialog = true;
  }

  private async retryReturnSync(returnRecord: any): Promise<void> {
    if (!this.purchaseOrder?.id || !returnRecord.id) return;
    this.retryingReturnSync = returnRecord.id;
    try {
      await post(`/purchase_orders/${this.purchaseOrder.id}/returns/${returnRecord.id}/retry-shopify-sync`);
      EventBus.$emit('show-snackbar', 'Inventory sync retry initiated', 'success');
      await this.loadReturnHistory();
      if (this.selectedReturn && this.selectedReturn.id === returnRecord.id) {
        const updated = this.returnHistory.find(r => r.id === returnRecord.id);
        if (updated) this.selectedReturn = updated;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retry inventory sync';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.retryingReturnSync = null;
    }
  }

  private async deleteReturn(): Promise<void> {
    if (!this.purchaseOrder?.id || !this.returnToDelete) return;
    this.deletingReturn = true;
    try {
      await del(`/purchase_orders/${this.purchaseOrder.id}/returns/${this.returnToDelete.id}`);
      EventBus.$emit('show-snackbar', 'Return deleted successfully', 'success');
      this.showDeleteReturnDialog = false;
      this.returnToDelete = null;
      await this.loadReturnHistory();
      await this.fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete return';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.deletingReturn = false;
    }
  }

  // Return helper methods
  private getReturnSyncStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: 'success',
      pending: 'info',
      partial: 'warning',
      failed: 'error',
      skipped: 'grey'
    };
    return colors[status] || 'grey';
  }

  private getReturnSyncStatusIcon(status: string): string {
    const icons: { [key: string]: string } = {
      synced: 'mdi-check-circle',
      pending: 'mdi-clock-outline',
      partial: 'mdi-alert',
      failed: 'mdi-close-circle',
      skipped: 'mdi-minus-circle'
    };
    return icons[status] || 'mdi-help-circle';
  }

  private getReturnSyncStatusIconColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: '#4CAF50',
      pending: '#59C8E4',
      partial: '#f57c00',
      failed: '#F44336',
      skipped: '#9E9E9E'
    };
    return colors[status] || '#9E9E9E';
  }

  private getReturnSyncStatusText(status: string): string {
    const texts: { [key: string]: string } = {
      synced: 'Synced',
      pending: 'Pending',
      partial: 'Partial',
      failed: 'Failed',
      skipped: 'Skipped'
    };
    return texts[status] || status;
  }

  private getReturnItemSyncStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: 'success',
      failed: 'error',
      pending: 'info'
    };
    return colors[status] || 'grey';
  }

  private getReturnItemSyncStatusText(status: string): string {
    const texts: { [key: string]: string } = {
      synced: 'Synced',
      failed: 'Failed',
      pending: 'Pending'
    };
    return texts[status] || status;
  }

  private shouldShowReturnRetryButton(returnRecord: any): boolean {
    const status = returnRecord.shopifyInventorySyncMetadata?.status;
    return status === 'failed' || status === 'partial';
  }

  private calculateTotalReturnQuantity(items: any[]): number {
    if (!items) return 0;
    return items.reduce((sum, item) => sum + (Number(item.returnedQuantity) || 0), 0);
  }

  private getReturnTotalValue(returnRecord: any): number {
    if (!returnRecord?.items || !this.purchaseOrder) return 0;
    return returnRecord.items.reduce((sum: number, returnItem: any) => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === returnItem.purchaseOrderItemId);
      const unitPrice = orderItem ? Number(orderItem.unitPrice) || 0 : 0;
      return sum + (Number(returnItem.returnedQuantity) || 0) * unitPrice;
    }, 0);
  }

  private getReturnDetailItems(returnRecord: any): any[] {
    if (!returnRecord || !returnRecord.items || !this.purchaseOrder) {
      return [];
    }

    // Map return items to include product details from purchaseOrder.items (same pattern as receive items)
    return returnRecord.items.map((returnItem: any) => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === returnItem.purchaseOrderItemId);
      if (!orderItem) return null;

      return {
        productName: orderItem.productName,
        variant: orderItem.variant || 'Default',
        sku: orderItem.sku || 'N/A',
        returnedQuantity: Number(returnItem.returnedQuantity) || 0,
        unitPrice: orderItem.unitPrice != null ? Number(orderItem.unitPrice) : null,
        shopifySyncStatus: returnItem.shopifyInventorySyncMetadata?.status || 'pending',
        inventoryItemId: returnItem.inventoryItemId, // For Track Inventory button
        variantId: returnItem.variantId
      };
    }).filter((item: any) => item !== null);
  }

  private showMessage(message: string, type: string): void {
    // Use existing notification system
    EventBus.$emit('show-message', { message, type });
  }
}
